import { useState, useEffect, useRef } from "react";
import {
  Activity,
  BarChart3,
  Bot,
  CheckCircle2,
  ClipboardList,
  FileText,
  Gift,
  LayoutDashboard,
  Lock,
  LogOut,
  MessageCircle,
  MessageSquare,
  Pencil,
  RefreshCw,
  Send,
  Shield,
  Smile,
  Smartphone,
  Ticket,
  Trash2,
  User,
  UserCog,
  UserRound,
  Users,
  XCircle,
} from "lucide-react";

const API = process.env.REACT_APP_API_URL || (
  typeof window !== "undefined" && window.location.hostname === "localhost"
    ? "http://localhost:3000/api"
    : "/api"
);

const COLORS = {
  bg: "#f6faf7", surface: "#ffffff", surface2: "#eef7f1",
  border: "rgba(19,127,95,0.14)", borderHard: "rgba(19,127,95,0.28)",
  green: "#1f9d74", greenDark: "#15795a", greenLight: "rgba(31,157,116,0.12)",
  text: "#163229", text2: "#4b665d", text3: "#7a9088",
  red: "#cf4a43", yellow: "#b98217", blue: "#2a7db8",
  purple: "#8b6bc5", orange: "#c06d2f"
};

const S = {
  app: { display:"flex", height:"100vh", background:`radial-gradient(circle at top right, #ffffff 0%, ${COLORS.bg} 60%)`, color:COLORS.text, fontFamily:"'Segoe UI',system-ui,sans-serif", overflow:"hidden", direction:"rtl" },
  sidebar: { width:220, minWidth:220, background:COLORS.surface, borderLeft:`1px solid ${COLORS.border}`, display:"flex", flexDirection:"column", padding:"20px 0", boxShadow:"-6px 0 20px rgba(20,62,48,0.05)" },
  navItem: (active) => ({ display:"flex", alignItems:"center", gap:10, padding:"9px 16px", cursor:"pointer", fontSize:13, color:active?COLORS.green:COLORS.text2, background:active?COLORS.greenLight:"transparent", borderRadius:8, margin:"0 8px 2px", transition:"all 0.15s", fontWeight:active?500:400 }),
  badge: (color="#25D366") => ({ background:color, color:"#000", fontSize:10, fontWeight:700, padding:"1px 7px", borderRadius:10, marginRight:"auto" }),
  main: { flex:1, overflow:"auto", background:"transparent" },
  page: { padding:"28px 32px" },
  pageTitle: { fontSize:22, fontWeight:600, color:COLORS.text, marginBottom:4 },
  pageSub: { fontSize:13, color:COLORS.text2, marginBottom:24 },
  card: { background:COLORS.surface, border:`1px solid ${COLORS.border}`, borderRadius:12, padding:"18px 20px", boxShadow:"0 8px 24px rgba(20,62,48,0.06)" },
  btn: (variant="ghost") => ({
    display:"inline-flex", alignItems:"center", gap:6,
    padding:"8px 16px", borderRadius:8, fontSize:13, cursor:"pointer", border:"none", fontWeight:500, transition:"all 0.15s",
    background: variant==="primary"?COLORS.green: variant==="danger"?"rgba(248,81,73,0.1)": variant==="blue"?"rgba(88,166,255,0.12)": variant==="yellow"?"rgba(210,153,34,0.12)": variant==="purple"?"rgba(188,140,255,0.12)": "transparent",
    color: variant==="primary"?"#000": variant==="danger"?COLORS.red: variant==="blue"?COLORS.blue: variant==="yellow"?COLORS.yellow: variant==="purple"?COLORS.purple: COLORS.text2,
    border: variant==="ghost"?`1px solid ${COLORS.border}`: variant==="danger"?`1px solid rgba(248,81,73,0.25)`: variant==="blue"?`1px solid rgba(88,166,255,0.25)`: variant==="yellow"?`1px solid rgba(210,153,34,0.25)`: variant==="purple"?`1px solid rgba(188,140,255,0.25)`:"none"
  }),
  input: { width:"100%", background:COLORS.surface2, border:`1px solid ${COLORS.border}`, borderRadius:8, padding:"10px 14px", color:COLORS.text, fontSize:13, outline:"none", fontFamily:"inherit", boxSizing:"border-box" },
  textarea: { width:"100%", background:COLORS.surface2, border:`1px solid ${COLORS.border}`, borderRadius:8, padding:"12px 14px", color:COLORS.text, fontSize:13, resize:"vertical", fontFamily:"inherit", lineHeight:1.6, outline:"none", boxSizing:"border-box" }
};

// ── Activity Logger ───────────────────────────────────────────
const logActivity = (user, action, details="", targetPhone="") => {
  const logs = JSON.parse(localStorage.getItem("activityLogs") || "[]");
  logs.unshift({ id:Date.now(), userId:user.id, userName:user.name, userRole:user.role, action, details, time:new Date().toISOString() });
  localStorage.setItem("activityLogs", JSON.stringify(logs.slice(0, 500)));
  // حفظ في السيرفر
  const token = localStorage.getItem("authToken");
  if (token) {
    fetch(`${API}/activity`, { method:"POST", headers:{ "Content-Type":"application/json", Authorization:`Bearer ${token}` }, body:JSON.stringify({ action, details, targetPhone }) }).catch(()=>{});
  }
};

// ── Auth helpers — كلمات المرور تُرسل للخادم فقط ──────────────
function getStoredSession() {
  try {
    const token = localStorage.getItem("authToken");
    const user = JSON.parse(localStorage.getItem("currentUser") || "null");
    return token && user ? { token, user } : null;
  } catch { return null; }
}

function setStoredSession(token, user) {
  localStorage.setItem("authToken", token);
  localStorage.setItem("currentUser", JSON.stringify(user));
}

function clearStoredSession() {
  localStorage.removeItem("authToken");
  localStorage.removeItem("currentUser");
}

// رأس Authorization للطلبات المحمية
function authHeaders() {
  const token = localStorage.getItem("authToken");
  return token ? { "Content-Type": "application/json", Authorization: `Bearer ${token}` }
               : { "Content-Type": "application/json" };
}

function authOnlyHeaders() {
  const token = localStorage.getItem("authToken");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function formatDisplayPhone(value) {
  const raw = String(value || "").trim();
  const digits = raw.replace(/\D/g, "");
  if (!digits) return raw || "-";
  if (/^9665\d{8}$/.test(digits)) return `+${digits}`;
  if (/^05\d{8}$/.test(digits)) return digits;
  if (digits.length >= 14 && !digits.startsWith("966")) {
    return `معرف واتساب ${digits.slice(0, 6)}...${digits.slice(-4)}`;
  }
  return `+${digits}`;
}

function getLivePhoneFromTicket(ticket) {
  const description = String(ticket?.description || "");
  const extracted = (description.match(/رقم الجوال:\s*(.+)/) || [])[1] || "";
  return extracted.trim() || String(ticket?.customer_phone || "");
}

const TICKET_TYPE_ORDER = ["صيانة", "تركيبات", "تأسيس", "غرفة_تبريد", "عروض_الاسعار", "شكوى", "أخرى"];

const TICKET_TYPE_ALIASES = {
  "عروض_الاسعار": "عروض_الاسعار",
  "مبيعات_عرض_سعر": "عروض_الاسعار",
  "تأسيس": "تأسيس",
  "تأسيس_نحاس": "تأسيس",
  "تركيبات": "تركيبات",
  "صيانة": "صيانة",
  "صيانة_تكييف": "صيانة",
  "صيانة_تبريد": "صيانة",
  "صيانة_غسالة": "صيانة",
  "صيانة_أجهزة": "صيانة",
  "شكوى": "شكوى",
  "شكوى_خدمة": "شكوى",
  "شكوى_توصيل": "شكوى",
  "ضمان": "شكوى",
  "غرفة_تبريد": "غرفة_تبريد",
};

function normalizeTicketType(value) {
  const raw = String(value || "").trim();
  if (!raw) return "أخرى";
  return TICKET_TYPE_ALIASES[raw] || raw;
}

function ticketTypeLabel(value) {
  return String(value || "أخرى").replace(/_/g, " ");
}

// ══════════════════════════════════════════════════════════════
// LOGIN PAGE
// ══════════════════════════════════════════════════════════════
function LoginPage({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleLogin = async () => {
    if (!email || !password) { setError("يرجى إدخال البريد وكلمة المرور"); return; }
    setLoading(true); setError("");
    try {
      const res = await fetch(`${API}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim().toLowerCase(), password }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || "بيانات الدخول غير صحيحة"); return; }
      setStoredSession(data.token, data.user);
      logActivity(data.user, "تسجيل دخول", "");
      onLogin(data.user);
    } catch {
      setError("تعذر الاتصال بالخادم. تحقق من الاتصال.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ display:"flex", height:"100vh", alignItems:"center", justifyContent:"center", background:COLORS.bg, direction:"rtl" }}>
      <div style={{ width:360 }}>
        <div style={{ textAlign:"center", marginBottom:32 }}>
          <div style={{ fontSize:24, fontWeight:700, color:COLORS.text }}>OmranHub</div>
          <div style={{ fontSize:13, color:COLORS.text2, marginTop:4 }}>نجوم العمران</div>
        </div>
        <div style={S.card}>
          <div style={{ fontSize:15, fontWeight:500, marginBottom:18 }}>تسجيل الدخول</div>
          <div style={{ marginBottom:12 }}>
            <div style={{ fontSize:12, color:COLORS.text2, marginBottom:5 }}>البريد الإلكتروني</div>
            <input style={S.input} type="email" placeholder="admin@ostar.sa" value={email} onChange={e=>setEmail(e.target.value)} onKeyDown={e=>e.key==="Enter"&&handleLogin()} />
          </div>
          <div style={{ marginBottom:16 }}>
            <div style={{ fontSize:12, color:COLORS.text2, marginBottom:5 }}>كلمة المرور</div>
            <input style={S.input} type="password" placeholder="••••••••" value={password} onChange={e=>setPassword(e.target.value)} onKeyDown={e=>e.key==="Enter"&&handleLogin()} />
          </div>
          {error && <div style={{ padding:"9px 13px", background:"rgba(248,81,73,0.1)", border:"1px solid rgba(248,81,73,0.25)", borderRadius:8, fontSize:12, color:COLORS.red, marginBottom:14 }}>{error}</div>}
          <button style={{ ...S.btn("primary"), width:"100%", justifyContent:"center", padding:"11px" }} onClick={handleLogin} disabled={loading}>
            {loading ? "جارٍ التحقق..." : "دخول"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// CONVERSATIONS — مع نظام التحويل وإغلاق المحادثة
// ══════════════════════════════════════════════════════════════
function Conversations({ currentUser, activeWhatsAppNumberId, activeWhatsAppNumberName, onSelectWhatsAppNumber }) {
  const [convs, setConvs] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [waNumbers, setWaNumbers] = useState([]);
  const [handoffs, setHandoffs] = useState(() => JSON.parse(localStorage.getItem("handoffs") || "{}"));
  const [manualMsg, setManualMsg] = useState("");
  const [sending, setSending] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryText, setSummaryText] = useState("");
  const [closedConvs, setClosedConvs] = useState(() => JSON.parse(localStorage.getItem("closedConvs") || "{}"));
  const [pausedPhones, setPausedPhones] = useState(new Set());
  const endRef = useRef(null);

  const saveHandoffs = (h) => { setHandoffs(h); localStorage.setItem("handoffs", JSON.stringify(h)); };
  const saveClosedConvs = (c) => { setClosedConvs(c); localStorage.setItem("closedConvs", JSON.stringify(c)); };

  const postChecked = async (path, payload) => {
    const res = await fetch(`${API}${path}`, {
      method: "POST",
      headers: authHeaders(),
      body: JSON.stringify(payload || {}),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(body?.error || `request_failed_${res.status}`);
    return body;
  };

  const getItemTime = (item) => {
    let raw = item?.created_at || "";
    // توحيد التوقيت: إذا ما فيه timezone نضيف Z (UTC)
    if (raw && !raw.endsWith("Z") && !/[+-]\d{2}:\d{2}$/.test(raw)) {
      raw = raw + "Z";
    }
    const ts = Date.parse(raw);
    return Number.isFinite(ts) ? ts : 0;
  };

  const sortChronologically = (items = []) => {
    return [...items]
      .map((item, idx) => ({ item, idx }))
      .sort((a, b) => {
        const tDiff = getItemTime(a.item) - getItemTime(b.item);
        if (tDiff !== 0) return tDiff;

        const aId = String(a.item?.id || "");
        const bId = String(b.item?.id || "");
        const idDiff = aId.localeCompare(bId);
        if (idDiff !== 0) return idDiff;

        return a.idx - b.idx;
      })
      .map((x) => x.item);
  };

  useEffect(() => {
    loadConvs();
    const interval = setInterval(loadConvs, 15000);
    return () => clearInterval(interval);
  }, [activeWhatsAppNumberId]);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/whatsapp-numbers`);
        const data = await res.json();
        setWaNumbers(Array.isArray(data) ? data : []);
      } catch {
        setWaNumbers([]);
      }
    })();
  }, []);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior:"smooth" }); }, [selected]);

  const loadConvs = async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (activeWhatsAppNumberId && activeWhatsAppNumberId !== "all") {
        qs.set("whatsappNumberId", activeWhatsAppNumberId);
      }
      const url = qs.toString() ? `${API}/conversations?${qs.toString()}` : `${API}/conversations`;
      const res = await fetch(url, { headers: authHeaders() });
      const data = await res.json();
      const messages = Array.isArray(data) ? data : (data.messages || []);
      const allMedia = Array.isArray(data) ? [] : (data.media || []);
      const contactMap = Array.isArray(data) ? {} : (data.contacts || {});
      // مزامنة حالة الإغلاق من السيرفر
      const serverClosedPhones = new Set(data.closedPhones || []);
      const serverHandoffPhones = new Set(data.handoffPhones || []);
      const currentClosed = JSON.parse(localStorage.getItem("closedConvs") || "{}");
      let closedChanged = false;
      for (const phone of Object.keys(currentClosed)) {
        if (!serverClosedPhones.has(phone)) {
          delete currentClosed[phone];
          closedChanged = true;
        }
      }
      if (closedChanged) saveClosedConvs(currentClosed);
      // مزامنة حالة التحويل
      const currentHandoffs = JSON.parse(localStorage.getItem("handoffs") || "{}");
      let handoffChanged = false;
      for (const phone of Object.keys(currentHandoffs)) {
        if (!serverHandoffPhones.has(phone)) {
          delete currentHandoffs[phone];
          handoffChanged = true;
        }
      }
      if (handoffChanged) saveHandoffs(currentHandoffs);
      // مزامنة حالة إيقاف البوت
      setPausedPhones(new Set(data.pausedPhones || []));
      const byPhone = {};
      messages.forEach(m => {
        if (!m.customer_phone) return;
        if (!byPhone[m.customer_phone]) byPhone[m.customer_phone] = { msgs:[], media:[] };
        byPhone[m.customer_phone].msgs.push(m);
      });
      allMedia.forEach(m => {
        if (!m.customer_phone) return;
        if (!byPhone[m.customer_phone]) byPhone[m.customer_phone] = { msgs:[], media:[] };
        byPhone[m.customer_phone].media.push(m);
      });
      const sorted = Object.entries(byPhone).map(([phone, d]) => {
        const sortedMsgs = sortChronologically(d.msgs);
        const sortedMedia = sortChronologically(d.media);
        const allItems = sortChronologically([
          ...sortedMsgs.map(m => ({ ...m, _type: "msg" })),
          ...sortedMedia.map(m => ({ ...m, _type: "media" })),
        ]);
        const lastItem = allItems[allItems.length - 1] || null;

        return {
          phone,
          msgs: sortedMsgs,
          media: sortedMedia,
          allItems,
          last: lastItem,
          contact: contactMap[phone] || { name: phone, photoUrl: null, phone },
        };
      }).sort((a, b) => getItemTime(b.last) - getItemTime(a.last));
      setConvs(sorted);
      setSelected((prev) => prev ? sorted.find((item) => item.phone === prev.phone) || null : null);
    } catch(e) {}
    setLoading(false);
  };

  // إيقاف/تفعيل البوت لمحادثة محددة
  const toggleBotPause = async (phone) => {
    try {
      const res = await fetch(`${API}/bot-pause/toggle`, {
        method: "POST",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ phone }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || "failed");
      setPausedPhones(prev => {
        const next = new Set(prev);
        if (body.paused) next.add(phone); else next.delete(phone);
        return next;
      });
    } catch (e) {
      alert(`❌ فشل تغيير حالة البوت: ${e.message}`);
    }
  };

  // التحويل لموظف بشري
  const activateHandoff = async (phone) => {
    try {
      await postChecked("/handoff/activate", { phone, agentId:currentUser.id, agentName:currentUser.name, phoneNumberId:activeWhatsAppNumberId === "all" ? "" : activeWhatsAppNumberId });
      const newHandoffs = { ...handoffs, [phone]: { agentId:currentUser.id, agentName:currentUser.name, startedAt:new Date().toISOString(), satisfied:null, awaitingSatisfaction:false } };
      saveHandoffs(newHandoffs);
    } catch (e) {
      alert(`❌ تعذر التحويل للموظف: ${e.message}`);
      return;
    }
    setSelected(prev => prev?.phone === phone ? { ...prev } : prev);
  };

  // إيقاف وضع الموظف
  const deactivateHandoff = (phone) => {
    const newHandoffs = { ...handoffs };
    delete newHandoffs[phone];
    saveHandoffs(newHandoffs);
  };

  // إرسال استطلاع الرضا
  const sendSatisfactionSurvey = async (phone) => {
    try {
      await postChecked("/handoff/satisfaction", { phone, phoneNumberId:activeWhatsAppNumberId === "all" ? "" : activeWhatsAppNumberId });
      const newHandoffs = { ...handoffs, [phone]: { ...handoffs[phone], awaitingSatisfaction:true } };
      saveHandoffs(newHandoffs);
      alert("✅ تم إرسال استطلاع الرضا للعميل");
    } catch(e) { alert(`❌ خطأ في إرسال الاستطلاع: ${e.message}`); }
  };

  // إغلاق المحادثة
  const closeConversation = async (phone, satisfied) => {
    const isHandoffMode = !!handoffs[phone];
    const phoneNumberId = activeWhatsAppNumberId === "all" ? "" : activeWhatsAppNumberId;

    if (isHandoffMode) {
      // ── وضع الموظف: تدفق الإغلاق الكامل مع رسالة للعميل ──
      if (satisfied === null) { alert("يرجى إرسال استطلاع الرضا أولاً"); return; }
      if (!satisfied && !window.confirm("العميل غير راضٍ — هل تريد إغلاق المحادثة؟")) return;
      try {
        await postChecked("/handoff/close", { phone, agentId:currentUser.id, agentName:currentUser.name, satisfied, phoneNumberId });
        const closure = { phone, agentId:currentUser.id, agentName:currentUser.name, satisfied, closedAt:new Date().toISOString() };
        saveClosedConvs({ ...closedConvs, [phone]: closure });
        deactivateHandoff(phone);
        setSelected(null);
      } catch (e) {
        alert(`❌ تعذر إغلاق المحادثة: ${e.message}`);
      }
    } else {
      // ── وضع AI: إغلاق مباشر بدون رسالة للعميل ──
      try {
        await postChecked("/conversation/close", { phone, phoneNumberId });
        const closure = { phone, agentId:currentUser.id, agentName:currentUser.name, satisfied:true, closedAt:new Date().toISOString() };
        saveClosedConvs({ ...closedConvs, [phone]: closure });
        setSelected(null);
      } catch (e) {
        alert(`❌ تعذر إغلاق المحادثة: ${e.message}`);
      }
    }
  };

  // إرسال رسالة يدوية
  const sendManualMessage = async () => {
    if (!manualMsg.trim() || !selected) return;
    setSending(true);
    try {
      await postChecked("/handoff/send", { phone:selected.phone, message:manualMsg, agentName:currentUser.name, phoneNumberId:activeWhatsAppNumberId === "all" ? "" : activeWhatsAppNumberId });
      setManualMsg("");
      await loadConvs();
    } catch(e) { alert(`❌ خطأ في إرسال رسالة الموظف: ${e.message}`); }
    setSending(false);
  };

  const buildSummaryPayload = (conv) => {
    const items = (conv?.allItems || [])
      .filter((i) => i?._type === "msg" && i?.content)
      .slice(-40)
      .map((i) => ({ role: i.role === "user" ? "user" : "assistant", content: String(i.content || "") }));
    return items;
  };

  const generateSmartSummary = async (conv = selected, opts = {}) => {
    const { silent = false } = opts || {};
    if (!conv) return;
    const payload = buildSummaryPayload(conv);
    if (!payload.length) {
      setSummaryText(silent ? "" : "لا توجد رسائل كافية للتلخيص.");
      return;
    }

    setSummaryLoading(true);
    try {
      const res = await fetch(`${API}/summarize`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ messages: payload }),
      });
      const body = await res.json().catch(() => ({}));
      setSummaryText(String(body.summary || "لا يوجد ملخص متاح حالياً."));
    } catch {
      setSummaryText("تعذر إنشاء التلخيص الذكي حالياً.");
    }
    setSummaryLoading(false);
  };

  useEffect(() => {
    if (!selected) {
      setSummaryText("");
      setSummaryLoading(false);
      return;
    }
    generateSmartSummary(selected, { silent: true });
  }, [selected?.phone, selected?.allItems?.length]);

  const getConvStatus = (phone) => {
    if (closedConvs[phone]) return "closed";
    if (handoffs[phone]) return "human";
    return "ai";
  };

  const statusLabel = (s) => s==="ai"?"البوت":s==="human"?"موظف":"مغلقة ✓";
  const statusColor = (s) => s==="ai"?COLORS.green:s==="human"?COLORS.blue:"#e53935";

  return (
    <div style={S.page}>
      <div style={S.pageTitle}>المحادثات</div>
      <div style={{ ...S.pageSub, marginBottom:16 }}>
        إدارة محادثات العملاء — التحويل والإغلاق
        <span style={{ marginRight:10, color:COLORS.text3 }}>
          الرقم المختار: {activeWhatsAppNumberId === "all" ? "كل الأرقام" : (activeWhatsAppNumberName || activeWhatsAppNumberId)}
        </span>
      </div>
      <div style={{ ...S.card, marginBottom:12, padding:"10px 12px", display:"flex", gap:10, alignItems:"center" }}>
        <div style={{ fontSize:12, color:COLORS.text2 }}>تصفية المحادثات حسب الرقم:</div>
        <select
          style={{ ...S.input, maxWidth:340, height:36, padding:"6px 10px" }}
          value={activeWhatsAppNumberId || "all"}
          onChange={(e) => {
            const selectedId = e.target.value;
            if (selectedId === "all") {
              onSelectWhatsAppNumber?.("all", "كل الأرقام");
              return;
            }
            const matched = waNumbers.find(n => String(n.phone_number_id) === String(selectedId));
            onSelectWhatsAppNumber?.(selectedId, matched?.name || selectedId);
          }}
        >
          <option value="all">كل الأرقام</option>
          {waNumbers.map((n) => {
            const visible = n.session_phone || n.phone_number || n.phone_number_id;
            return (
              <option key={n.id} value={n.phone_number_id}>
                {n.name} - {formatDisplayPhone(visible)}
              </option>
            );
          })}
        </select>
      </div>

      {(summaryLoading || summaryText) && (
        <div style={{ ...S.card, marginBottom:12, padding:"10px 12px", border:`1px solid rgba(188,140,255,0.25)`, background:"rgba(188,140,255,0.08)" }}>
          <div style={{ fontSize:11, color:COLORS.purple, marginBottom:4, display:"flex", alignItems:"center", gap:6 }}>
            <Bot size={13} /> ملخص المحادثة
          </div>
          <div style={{ fontSize:12, lineHeight:1.8, color:COLORS.text2 }}>
            {summaryLoading ? "جارٍ تجهيز الملخص..." : summaryText}
          </div>
        </div>
      )}

      <div style={{ display:"grid", gridTemplateColumns:"290px 1fr", border:`1px solid ${COLORS.border}`, borderRadius:12, overflow:"hidden", height:"calc(100vh - 160px)" }}>

        {/* قائمة المحادثات */}
        <div style={{ background:COLORS.surface, borderLeft:`1px solid ${COLORS.border}`, display:"flex", flexDirection:"column", overflow:"hidden" }}>
          <div style={{ padding:"12px 16px", borderBottom:`1px solid ${COLORS.border}`, fontSize:12, color:COLORS.text2, display:"flex", justifyContent:"space-between", flexShrink:0 }}>
            <span>{convs.length} محادثة</span>
            <button style={{ ...S.btn("ghost"), padding:"3px 8px", fontSize:11 }} onClick={loadConvs}>تحديث</button>
          </div>
          <div style={{ flex:1, overflowY:"auto" }}>
            {loading ? <div style={{ padding:30, textAlign:"center", color:COLORS.text2 }}>جارٍ التحميل...</div>
            : convs.length===0 ? <div style={{ padding:30, textAlign:"center", color:COLORS.text3 }}>لا توجد محادثات</div>
            : convs.map((c,i) => {
              const status = getConvStatus(c.phone);
              return (
                <div key={i} onClick={() => setSelected(c)} style={{ padding:"12px 14px", cursor:"pointer", borderBottom:`1px solid ${COLORS.border}`, background:selected?.phone===c.phone?COLORS.greenLight:"transparent", borderRight:selected?.phone===c.phone?`2px solid ${COLORS.green}`:"2px solid transparent" }}>
                  <div style={{ display:"flex", justifyContent:"space-between", marginBottom:5, gap:10 }}>
                    <div style={{ display:"flex", alignItems:"center", gap:10, minWidth:0 }}>
                      {c.contact?.photoUrl ? (
                        <img src={c.contact.photoUrl} alt={c.contact?.name || c.phone} style={{ width:34, height:34, borderRadius:"50%", objectFit:"cover", border:`1px solid ${COLORS.border}` }} />
                      ) : (
                        <div style={{ width:34, height:34, borderRadius:"50%", background:COLORS.greenLight, border:`1px solid ${COLORS.borderHard}`, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                          <UserRound size={15} color={COLORS.green} />
                        </div>
                      )}
                      <div style={{ minWidth:0 }}>
                        <div style={{ fontSize:13, fontWeight:500, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{c.contact?.name || c.phone}</div>
                        <div style={{ fontSize:11, color:COLORS.text3, direction:"ltr" }}>الرقم: {formatDisplayPhone(c.contact?.phone || c.phone)}</div>
                      </div>
                    </div>
                    <span style={{ fontSize:10, padding:"2px 7px", borderRadius:10, background:`${statusColor(status)}20`, color:statusColor(status), border:`1px solid ${statusColor(status)}40` }}>
                      {statusLabel(status)}
                    </span>
                  </div>
                  <div style={{ fontSize:12, color:COLORS.text2, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                    {c.last?.content?.substring(0,50) || "—"}
                  </div>
                  <div style={{ fontSize:10, color:COLORS.text3, marginTop:3 }}>
                    {c.last?.created_at ? new Date(c.last.created_at).toLocaleString("ar-SA") : ""}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* تفاصيل المحادثة */}
        <div style={{ display:"flex", flexDirection:"column", overflow:"hidden" }}>
          {!selected ? (
            <div style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", height:"100%", opacity:0.4 }}>
              <MessageSquare size={32} />
              <div style={{ color:COLORS.text2, marginTop:8 }}>اختر محادثة</div>
            </div>
          ) : (() => {
            const status = getConvStatus(selected.phone);
            const handoff = handoffs[selected.phone];
            const closure = closedConvs[selected.phone];
            return (
              <>
                {/* هيدر */}
                <div style={{ padding:"12px 16px", borderBottom:`1px solid ${COLORS.border}`, background:COLORS.surface, flexShrink:0 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:10 }}>
                    {selected.contact?.photoUrl ? (
                      <img src={selected.contact.photoUrl} alt={selected.contact?.name || selected.phone} style={{ width:42, height:42, borderRadius:"50%", objectFit:"cover", border:`1px solid ${COLORS.border}` }} />
                    ) : (
                      <div style={{ width:42, height:42, borderRadius:"50%", background:COLORS.greenLight, border:`1px solid ${COLORS.borderHard}`, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                        <UserRound size={18} color={COLORS.green} />
                      </div>
                    )}
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:14, fontWeight:500 }}>{selected.contact?.name || selected.phone}</div>
                      <div style={{ fontSize:11, color:COLORS.text3, direction:"ltr" }}>الرقم: {formatDisplayPhone(selected.contact?.phone || selected.phone)}</div>
                      <div style={{ fontSize:11, color:COLORS.text2 }}>{selected.msgs.length} رسالة</div>
                    </div>
                    <span style={{ fontSize:11, padding:"3px 10px", borderRadius:10, background:`${statusColor(status)}20`, color:statusColor(status), border:`1px solid ${statusColor(status)}40` }}>
                      {statusLabel(status)}
                    </span>
                  </div>

                  {/* أزرار الإجراءات */}
                  {status === "ai" && !closure && (() => {
                    const isBotPaused = pausedPhones.has(selected.phone);
                    return (
                      <div style={{ display:"flex", gap:8, flexWrap:"wrap", alignItems:"center" }}>
                        <button
                          style={{ ...S.btn(isBotPaused ? "primary" : "yellow"), padding:"6px 12px", fontSize:12 }}
                          onClick={() => toggleBotPause(selected.phone)}
                          title={isBotPaused ? "البوت موقف — اضغط لتفعيله" : "اضغط لإيقاف البوت عن الرد"}
                        >
                          <Bot size={14} /> {isBotPaused ? "تفعيل البوت" : "إيقاف البوت"}
                        </button>
                        {!isBotPaused && (
                          <>
                            <button style={{ ...S.btn("blue"), padding:"6px 12px", fontSize:12 }} onClick={() => activateHandoff(selected.phone)}>
                              <UserCog size={14} /> تحويل لموظف
                            </button>
                            <button style={{ ...S.btn("purple"), padding:"6px 12px", fontSize:12 }} onClick={generateSmartSummary} disabled={summaryLoading}>
                              <Bot size={14} /> {summaryLoading ? "جارٍ التلخيص..." : "تلخيص ذكي"}
                            </button>
                          </>
                        )}
                        <button style={{ ...S.btn("danger"), padding:"6px 12px", fontSize:12 }} onClick={() => { if(window.confirm("هل تريد إغلاق هذه المحادثة؟")) closeConversation(selected.phone, true); }}>
                          <XCircle size={14} /> إغلاق المحادثة
                        </button>
                        {isBotPaused && (
                          <span style={{ fontSize:11, color:COLORS.yellow, background:"rgba(210,153,34,0.12)", border:"1px solid rgba(210,153,34,0.3)", borderRadius:8, padding:"3px 10px" }}>
                            ⏸ البوت موقف — الرسائل محفوظة بدون رد
                          </span>
                        )}
                      </div>
                    );
                  })()}

                  {status === "human" && (
                    <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                      <button style={{ ...S.btn("yellow"), padding:"6px 12px", fontSize:12 }} onClick={() => sendSatisfactionSurvey(selected.phone)}>
                        <ClipboardList size={14} /> استطلاع الرضا
                      </button>
                      <button style={{ ...S.btn("primary"), padding:"6px 12px", fontSize:12 }} onClick={() => closeConversation(selected.phone, handoff?.satisfied ?? true)}
                        disabled={handoff?.awaitingSatisfaction && handoff?.satisfied === null}>
                        <CheckCircle2 size={14} /> إغلاق — راضٍ
                      </button>
                      <button style={{ ...S.btn("danger"), padding:"6px 12px", fontSize:12 }} onClick={() => closeConversation(selected.phone, false)}>
                        <XCircle size={14} /> إغلاق — غير راضٍ
                      </button>
                    </div>
                  )}

                  {status === "closed" && closure && (
                    <div style={{ padding:"8px 12px", background:COLORS.surface2, borderRadius:8, fontSize:12, color:COLORS.text2 }}>
                      أُغلقت بواسطة <b style={{ color:COLORS.text }}>{closure.agentName}</b> — رضا العميل: {closure.satisfied?"راضٍ":"غير راضٍ"}
                      <span style={{ marginRight:8, color:COLORS.text3 }}>{new Date(closure.closedAt).toLocaleString("ar-SA")}</span>
                    </div>
                  )}
                </div>

                {/* الرسائل */}
                <div style={{ flex:1, overflowY:"auto", padding:"14px 18px", display:"flex", flexDirection:"column", gap:10, minHeight:0, background:COLORS.surface2 }}>
                  {selected.allItems.map((item, idx) => {
                    if (item._type === "media") {
                      const mediaLabel = item.caption || item.mime_type || item.media_type || "ملف";
                      if (!item.media_url) {
                        return null; // تخطي الميديا بدون رابط
                      }

                      if (item.media_type === "image" && item.media_url) {
                        return (
                          <div key={idx} style={{ maxWidth:"65%", alignSelf:"flex-start", background:COLORS.surface, border:`1px solid ${COLORS.border}`, borderRadius:12, padding:"10px 12px" }}>
                            <div style={{ fontSize:10, color:COLORS.text3, marginBottom:6, display:"flex", alignItems:"center", gap:4 }}><UserRound size={12} /> العميل</div>
                            <img src={item.media_url} alt="صورة مرسلة" style={{ maxWidth:"100%", maxHeight:220, borderRadius:8, cursor:"pointer" }}
                              onClick={() => window.open(item.media_url, "_blank")} onError={e=>e.target.style.display="none"} />
                            {item.caption && <div style={{ fontSize:12, color:COLORS.text2, marginTop:6 }}>{item.caption}</div>}
                            <div style={{ fontSize:9, color:COLORS.text3, marginTop:4 }}>
                              {item.created_at ? new Date(item.created_at).toLocaleString("ar-SA",{year:"numeric",month:"2-digit",day:"2-digit",hour:"2-digit",minute:"2-digit"}) : ""}
                            </div>
                          </div>
                        );
                      }

                      if (["video"].includes(item.media_type)) {
                        return (
                          <div key={idx} style={{ maxWidth:"70%", alignSelf:"flex-start", background:COLORS.surface, border:`1px solid ${COLORS.border}`, borderRadius:12, padding:"10px 12px" }}>
                            <div style={{ fontSize:10, color:COLORS.text3, marginBottom:6, display:"flex", alignItems:"center", gap:4 }}><UserRound size={12} /> العميل</div>
                            <video src={item.media_url} controls style={{ width:"100%", borderRadius:8, maxHeight:280 }} />
                            {item.caption && <div style={{ fontSize:12, color:COLORS.text2, marginTop:4 }}>{item.caption}</div>}
                            <div style={{ marginTop:4, display:"flex", justifyContent:"space-between", alignItems:"center", gap:8 }}>
                              <div style={{ fontSize:9, color:COLORS.text3 }}>
                                {item.created_at ? new Date(item.created_at).toLocaleString("ar-SA",{year:"numeric",month:"2-digit",day:"2-digit",hour:"2-digit",minute:"2-digit"}) : ""}
                              </div>
                              <a href={item.media_url} target="_blank" rel="noreferrer" style={{ fontSize:11, color:COLORS.blue, textDecoration:"none" }}>فتح</a>
                            </div>
                          </div>
                        );
                      }

                      if (["audio", "ptt"].includes(item.media_type)) {
                        return (
                          <div key={idx} style={{ maxWidth:"70%", alignSelf:"flex-start", background:COLORS.surface, border:`1px solid ${COLORS.border}`, borderRadius:12, padding:"10px 12px" }}>
                            <div style={{ fontSize:10, color:COLORS.text3, marginBottom:6, display:"flex", alignItems:"center", gap:4 }}><UserRound size={12} /> العميل</div>
                            <div style={{ fontSize:12, color:COLORS.blue, display:"flex", alignItems:"center", gap:6, marginBottom:8 }}><FileText size={14} /> مقطع صوتي</div>
                            <audio src={item.media_url} controls style={{ width:"100%" }} />
                            {item.caption && <div style={{ fontSize:12, color:COLORS.text2, marginTop:4 }}>{item.caption}</div>}
                            <div style={{ marginTop:4, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                              <div style={{ fontSize:9, color:COLORS.text3 }}>
                                {item.created_at ? new Date(item.created_at).toLocaleString("ar-SA",{year:"numeric",month:"2-digit",day:"2-digit",hour:"2-digit",minute:"2-digit"}) : ""}
                              </div>
                              <a href={item.media_url} target="_blank" rel="noreferrer" style={{ fontSize:11, color:COLORS.blue, textDecoration:"none" }}>فتح</a>
                            </div>
                          </div>
                        );
                      }

                      return (
                        <div key={idx} style={{ maxWidth:"70%", alignSelf:"flex-start", background:COLORS.surface, border:`1px solid ${COLORS.border}`, borderRadius:12, padding:"10px 12px" }}>
                          <div style={{ fontSize:10, color:COLORS.text3, marginBottom:6, display:"flex", alignItems:"center", gap:4 }}><UserRound size={12} /> العميل</div>
                          <div style={{ fontSize:12, color:COLORS.blue, display:"flex", alignItems:"center", gap:6 }}><FileText size={14} /> مستند</div>
                          <div style={{ fontSize:11, color:COLORS.text2, marginTop:5, wordBreak:"break-word" }}>{mediaLabel}</div>
                          {item.caption && <div style={{ fontSize:12, color:COLORS.text2, marginTop:4 }}>{item.caption}</div>}
                          <div style={{ marginTop:10, display:"flex", justifyContent:"space-between", alignItems:"center", gap:12 }}>
                            <div style={{ fontSize:9, color:COLORS.text3 }}>
                              {item.created_at ? new Date(item.created_at).toLocaleString("ar-SA",{year:"numeric",month:"2-digit",day:"2-digit",hour:"2-digit",minute:"2-digit"}) : ""}
                            </div>
                            <div style={{ display:"flex", gap:12 }}>
                              <a href={item.media_url} target="_blank" rel="noreferrer" style={{ fontSize:12, color:COLORS.blue, textDecoration:"none" }}>فتح المستند</a>
                              <a href={item.media_url} download style={{ fontSize:12, color:COLORS.green, textDecoration:"none" }}>تحميل</a>
                            </div>
                          </div>
                        </div>
                      );
                    }

                    // كشف رسائل الميديا القديمة المحفوظة كنص (📷 صورة / 🎥 فيديو / 🎤 صوت / 📄 مستند)
                    const mediaEmojiMatch = item._type === "msg" && item.role === "user" && /^(📷|🎥|🎤|📄|📎)\s/.test(item.content || "");
                    if (mediaEmojiMatch) {
                      // ابحث عن ميديا مطابقة في نفس الوقت تقريباً (±30 ثانية)
                      const itemTime = new Date(item.created_at).getTime();
                      const matchingMedia = selected.media?.find(m =>
                        m.media_url && Math.abs(new Date(m.created_at).getTime() - itemTime) < 30000
                      );
                      if (matchingMedia) {
                        return null; // تخطي — الميديا الفعلية ستعرض من جدول media
                      }
                    }

                    // كشف رسالة موظف
                    const isAgentMsg = item.content?.startsWith("[") && item.role==="assistant";
                    const isAiMsg = item.role==="assistant" && !isAgentMsg;

                    return (
                      <div key={idx} style={{ display:"flex", justifyContent:item.role==="user"?"flex-start":"flex-end" }}>
                        <div style={{ maxWidth:"68%", padding:"10px 14px", borderRadius:14, fontSize:13, lineHeight:1.65,
                          background: item.role==="user"?COLORS.surface2: isAgentMsg?"rgba(88,166,255,0.12)":COLORS.greenLight,
                          border:`1px solid ${item.role==="user"?COLORS.border: isAgentMsg?"rgba(88,166,255,0.3)":COLORS.borderHard}`,
                          color:COLORS.text }}>
                          {isAiMsg && <div style={{ fontSize:10, color:COLORS.green, marginBottom:3, display:"flex", alignItems:"center", gap:4 }}><Bot size={12} />بوت</div>}
                          {isAgentMsg && <div style={{ fontSize:10, color:COLORS.blue, marginBottom:3, display:"flex", alignItems:"center", gap:4 }}><UserCog size={12} /> {item.content.match(/\[(.+?)\]/)?.[1] || "موظف"}</div>}
                          {item.role==="user" && <div style={{ fontSize:10, color:COLORS.text3, marginBottom:3, display:"flex", alignItems:"center", gap:4 }}><UserRound size={12} /> العميل</div>}
                          {isAgentMsg ? item.content.replace(/^\[.+?\]:\s*/, "") : item.content}
                          <div style={{ fontSize:9, color:COLORS.text3, marginTop:4 }}>
                            {item.created_at ? new Date(item.created_at).toLocaleString("ar-SA",{year:"numeric",month:"2-digit",day:"2-digit",hour:"2-digit",minute:"2-digit"}) : ""}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={endRef} />
                </div>

                {/* إدخال الرسالة — فقط في وضع الموظف */}
                {status === "human" && (
                  <div style={{ padding:"12px 16px", borderTop:`1px solid ${COLORS.border}`, display:"flex", gap:8, background:COLORS.surface, flexShrink:0 }}>
                    <div style={{ flex:1, position:"relative" }}>
                      <input style={{ ...S.input }} placeholder="اكتب رسالة كموظف..." value={manualMsg} onChange={e=>setManualMsg(e.target.value)} onKeyDown={e=>e.key==="Enter"&&!e.shiftKey&&sendManualMessage()} />
                    </div>
                    <button style={{ ...S.btn("primary"), padding:"0 18px", flexShrink:0 }} onClick={sendManualMessage} disabled={sending || !manualMsg.trim()}>
                      {sending ? "..." : "إرسال"}
                    </button>
                  </div>
                )}

                {status === "ai" && (
                  <div style={{ padding:"10px 16px", borderTop:`1px solid ${COLORS.border}`, background:COLORS.surface, flexShrink:0, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                    <span style={{ fontSize:12, color:COLORS.text3 }}>البوت يرد تلقائياً</span>
                    <button style={{ ...S.btn("blue"), padding:"5px 12px", fontSize:12 }} onClick={() => activateHandoff(selected.phone)}>
                      <UserCog size={14} /> تدخل كموظف
                    </button>
                  </div>
                )}
              </>
            );
          })()}
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// DASHBOARD
// ══════════════════════════════════════════════════════════════
function Dashboard({ onNav }) {
  const [stats, setStats] = useState({ totalMsgs:0, openTickets:0, totalTickets:0, totalMedia:0, ticketTypeKpi:{} });
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const closedConvs = JSON.parse(localStorage.getItem("closedConvs") || "{}");
  const closedCount = Object.keys(closedConvs).length;
  const satisfiedCount = Object.values(closedConvs).filter(c=>c.satisfied).length;

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setLoadError("");

    Promise.all([
      fetch(`${API}/stats`).then(async (r) => {
        const body = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(body?.error || "stats_fetch_failed");
        return body;
      }),
      fetch(`${API}/tickets`).then(async (r) => {
        const body = await r.json().catch(() => ([]));
        if (!r.ok) throw new Error("tickets_fetch_failed");
        return Array.isArray(body) ? body : [];
      }),
    ])
      .then(([statsBody, ticketList]) => {
        if (!mounted) return;
        setStats(statsBody || {});
        setTickets(ticketList.slice(0, 5));
      })
      .catch(() => {
        if (!mounted) return;
        setLoadError("تعذر تحميل بيانات لوحة التحكم. تأكد أن الباكند يعمل وأن رابط API صحيح.");
        setStats({ totalMsgs:0, openTickets:0, totalTickets:0, totalMedia:0, ticketTypeKpi:{} });
        setTickets([]);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => { mounted = false; };
  }, []);

  const statCards = [
    { label:"إجمالي الرسائل", value:stats.totalMsgs||0, icon:MessageCircle, color:COLORS.blue },
    { label:"تذاكر مفتوحة", value:stats.openTickets||0, icon:Ticket, color:COLORS.yellow },
    { label:"استفسارات تم الرد عليها", value:stats.repliedInquiries||0, icon:CheckCircle2, color:COLORS.greenDark },
    { label:"محادثات مغلقة", value:closedCount, icon:Lock, color:COLORS.green },
    { label:"عملاء راضون", value:`${closedCount>0?Math.round(satisfiedCount/closedCount*100):0}%`, icon:Smile, color:COLORS.purple },
  ];

  return (
    <div style={S.page}>
      <div style={S.pageTitle}>لوحة التحكم</div>
      <div style={{ ...S.pageSub }}>ملخص نشاط النظام</div>
      {loadError && (
        <div style={{ ...S.card, marginBottom:12, border:`1px solid rgba(207,74,67,0.35)`, background:"rgba(207,74,67,0.08)", color:COLORS.red }}>
          {loadError}
        </div>
      )}
      {loading && (
        <div style={{ ...S.card, marginBottom:12, color:COLORS.text2 }}>جارٍ تحميل البيانات...</div>
      )}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(5,1fr)", gap:14, marginBottom:24 }}>
        {statCards.map(s => (
          <div key={s.label} style={S.card}>
            <div style={{ marginBottom:8 }}><s.icon size={22} color={s.color} /></div>
            <div style={{ fontSize:11, color:COLORS.text2, marginBottom:4 }}>{s.label}</div>
            <div style={{ fontSize:26, fontWeight:600, color:s.color }}>{s.value}</div>
          </div>
        ))}
      </div>
      <div style={{ ...S.card, marginBottom:16, padding:"12px 14px" }}>
        <div style={{ fontSize:13, fontWeight:600, marginBottom:10, color:COLORS.text2 }}>KPI التذاكر حسب النوع</div>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(7,minmax(0,1fr))", gap:8 }}>
          {TICKET_TYPE_ORDER.map((type) => (
            <div key={type} style={{ border:`1px solid ${COLORS.border}`, borderRadius:10, padding:"8px 10px", background:COLORS.surface2 }}>
              <div style={{ fontSize:11, color:COLORS.text2, marginBottom:3 }}>{ticketTypeLabel(type)}</div>
              <div style={{ fontSize:18, fontWeight:700, color:COLORS.green }}>{Number(stats?.ticketTypeKpi?.[type] || 0)}</div>
            </div>
          ))}
        </div>
      </div>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>
        <div style={S.card}>
          <div style={{ fontSize:14, fontWeight:500, marginBottom:14, color:COLORS.text2 }}>آخر التذاكر</div>
          {tickets.length===0 ? <div style={{ color:COLORS.text3, textAlign:"center", padding:20 }}>لا توجد تذاكر</div>
          : tickets.map(t=>(
            <div key={t.id} onClick={()=>onNav("tickets")} style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 0", borderBottom:`1px solid ${COLORS.border}`, cursor:"pointer" }}>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:13, fontWeight:500, color:COLORS.green }}>{t.ticket_number}</div>
                <div style={{ fontSize:11, color:COLORS.text2, direction:"ltr" }}>{formatDisplayPhone(getLivePhoneFromTicket(t))}</div>
              </div>
              <span style={{ fontSize:11, padding:"2px 9px", borderRadius:10, background:COLORS.greenLight, color:COLORS.green, border:`1px solid ${COLORS.borderHard}` }}>{t.status}</span>
            </div>
          ))}
        </div>
        <div style={S.card}>
          <div style={{ fontSize:14, fontWeight:500, marginBottom:14, color:COLORS.text2 }}>آخر الأنشطة</div>
          {JSON.parse(localStorage.getItem("activityLogs")||"[]").slice(0,5).map((log,i)=>(
            <div key={i} style={{ display:"flex", gap:10, padding:"9px 0", borderBottom:`1px solid ${COLORS.border}` }}>
              <div style={{ width:30, height:30, borderRadius:"50%", background:COLORS.greenLight, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}><Activity size={14} color={COLORS.green} /></div>
              <div>
                <div style={{ fontSize:12, color:COLORS.text }}><b>{log.userName}</b> — {log.action}</div>
                <div style={{ fontSize:11, color:COLORS.text3 }}>{new Date(log.time).toLocaleString("ar-SA")}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// REPORTS — مع إغلاق المحادثات
// ══════════════════════════════════════════════════════════════
function Reports() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("الكل");
  const closedConvs = JSON.parse(localStorage.getItem("closedConvs") || "{}");
  const closures = Object.values(closedConvs);

  useEffect(() => {
    setLoading(true);
    fetch(`${API}/tickets`).then(r=>r.json()).then(d=>setTickets(Array.isArray(d)?d:[])).catch(()=>setTickets([])).finally(()=>setLoading(false));
  }, []);

  const filtered = period==="الكل" ? tickets : tickets.filter(t=>{
    const d=new Date(t.created_at); const now=new Date();
    if(period==="اليوم") return d.toDateString()===now.toDateString();
    if(period==="الأسبوع") return (now-d)<7*24*60*60*1000;
    if(period==="الشهر") return d.getMonth()===now.getMonth();
    return true;
  });

  const salesTickets = filtered.filter(t => ["عروض_الاسعار", "مبيعات_عرض_سعر"].includes(t.type));

  const parseSalesTicketDetails = (description = "") => {
    const txt = String(description || "");
    const name = (txt.match(/الاسم:\s*(.+)/) || [])[1] || "-";
    const phone = (txt.match(/رقم الجوال:\s*(.+)/) || [])[1] || "-";
    const request = (txt.match(/المطلوب:\s*(.+)/) || [])[1] || "-";
    const quantity = (txt.match(/الكمية:\s*(.+)/) || [])[1] || "-";
    return { name: name.trim(), phone: phone.trim(), request: request.trim(), quantity: quantity.trim() };
  };

  const satisfied = closures.filter(c=>c.satisfied).length;
  const unsatisfied = closures.filter(c=>!c.satisfied).length;

  return (
    <div style={S.page}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:4 }}>
        <div style={S.pageTitle}>التقارير</div>
        <div style={{ display:"flex", gap:8 }}>
          {["اليوم","الأسبوع","الشهر","الكل"].map(p=>(
            <button key={p} style={{ ...S.btn(period===p?"primary":"ghost"), padding:"5px 12px", fontSize:12 }} onClick={()=>setPeriod(p)}>{p}</button>
          ))}
        </div>
      </div>
      <div style={{ ...S.pageSub }}>تقرير الأداء وإغلاق المحادثات</div>

      <div style={{ display:"grid", gridTemplateColumns:"repeat(5,1fr)", gap:14, marginBottom:24 }}>
        {[
          { label:"إجمالي التذاكر", value:filtered.length, color:COLORS.blue, icon:Ticket },
          { label:"طلبات عرض السعر", value:salesTickets.length, color:COLORS.orange, icon:FileText },
          { label:"محادثات مغلقة", value:closures.length, color:COLORS.green, icon:Lock },
          { label:"عملاء راضون", value:satisfied, color:COLORS.purple, icon:Smile },
          { label:"عملاء غير راضين", value:unsatisfied, color:COLORS.red, icon:XCircle },
        ].map(s=>(
          <div key={s.label} style={S.card}>
            <div style={{ marginBottom:8 }}><s.icon size={22} color={s.color} /></div>
            <div style={{ fontSize:11, color:COLORS.text2, marginBottom:4 }}>{s.label}</div>
            <div style={{ fontSize:26, fontWeight:600, color:s.color }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* قسم مستقل لطلبات عرض السعر */}
      <div style={{ ...S.card, marginBottom:20 }}>
        <div style={{ fontSize:14, fontWeight:500, marginBottom:14, color:COLORS.text2 }}>طلبات عرض السعر ({salesTickets.length})</div>
        <table style={{ width:"100%", borderCollapse:"collapse" }}>
          <thead>
            <tr style={{ borderBottom:`1px solid ${COLORS.border}` }}>
              {["رقم التذكرة","الاسم","رقم الجوال","المطلوب","الكمية","التاريخ"].map(h=>(
                <th key={h} style={{ padding:"10px 12px", textAlign:"right", fontSize:12, color:COLORS.text2, fontWeight:500 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {salesTickets.length===0 ? <tr><td colSpan={6} style={{ textAlign:"center", padding:24, color:COLORS.text3 }}>لا توجد طلبات عرض سعر</td></tr>
            : salesTickets.map(t=>{
              const details = parseSalesTicketDetails(t.description);
              return (
                <tr key={t.id} style={{ borderBottom:`1px solid ${COLORS.border}` }}>
                  <td style={{ padding:"11px 12px", fontSize:13, color:COLORS.green, fontWeight:500 }}>{t.ticket_number}</td>
                  <td style={{ padding:"11px 12px", fontSize:12 }}>{details.name}</td>
                  <td style={{ padding:"11px 12px", fontSize:12, direction:"ltr", color:COLORS.text2 }}>{formatDisplayPhone(details.phone)}</td>
                  <td style={{ padding:"11px 12px", fontSize:12, color:COLORS.text }}>{details.request}</td>
                  <td style={{ padding:"11px 12px", fontSize:12, color:COLORS.text2 }}>{details.quantity}</td>
                  <td style={{ padding:"11px 12px", fontSize:12, color:COLORS.text2 }}>{new Date(t.created_at).toLocaleDateString("ar-SA")}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* جدول إغلاق المحادثات */}
      <div style={{ ...S.card, marginBottom:20 }}>
        <div style={{ fontSize:14, fontWeight:500, marginBottom:14, color:COLORS.text2, display:"flex", alignItems:"center", gap:6 }}><Lock size={16} /> سجل إغلاق المحادثات</div>
        {closures.length===0 ? <div style={{ color:COLORS.text3, textAlign:"center", padding:20 }}>لا توجد محادثات مغلقة بعد</div>
        : (
          <table style={{ width:"100%", borderCollapse:"collapse" }}>
            <thead>
              <tr style={{ borderBottom:`1px solid ${COLORS.border}` }}>
                {["رقم العميل","الموظف","رضا العميل","وقت الإغلاق"].map(h=>(
                  <th key={h} style={{ padding:"10px 12px", textAlign:"right", fontSize:12, color:COLORS.text2, fontWeight:500 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {closures.map((c,i)=>(
                <tr key={i} style={{ borderBottom:`1px solid ${COLORS.border}` }}>
                  <td style={{ padding:"11px 12px", fontSize:13, direction:"ltr" }}>{c.phone}</td>
                  <td style={{ padding:"11px 12px", fontSize:13 }}>{c.agentName}</td>
                  <td style={{ padding:"11px 12px" }}>
                    <span style={{ fontSize:12, padding:"3px 10px", borderRadius:10, background:c.satisfied?"rgba(37,211,102,0.12)":"rgba(248,81,73,0.1)", color:c.satisfied?COLORS.green:COLORS.red, border:`1px solid ${c.satisfied?COLORS.borderHard:"rgba(248,81,73,0.25)"}` }}>
                      {c.satisfied?"✅ راضٍ":"❌ غير راضٍ"}
                    </span>
                  </td>
                  <td style={{ padding:"11px 12px", fontSize:12, color:COLORS.text2 }}>{new Date(c.closedAt).toLocaleString("ar-SA")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* جدول التذاكر */}
      <div style={S.card}>
        <div style={{ fontSize:14, fontWeight:500, marginBottom:14, color:COLORS.text2 }}>التذاكر ({filtered.length})</div>
        <table style={{ width:"100%", borderCollapse:"collapse" }}>
          <thead>
            <tr style={{ borderBottom:`1px solid ${COLORS.border}` }}>
              {["رقم التذكرة","العميل","النوع","الحالة","التاريخ"].map(h=>(
                <th key={h} style={{ padding:"10px 12px", textAlign:"right", fontSize:12, color:COLORS.text2, fontWeight:500 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.length===0 ? <tr><td colSpan={5} style={{ textAlign:"center", padding:24, color:COLORS.text3 }}>لا توجد تذاكر</td></tr>
            : filtered.map(t=>(
              <tr key={t.id} style={{ borderBottom:`1px solid ${COLORS.border}` }}>
                <td style={{ padding:"11px 12px", fontSize:13, color:COLORS.green, fontWeight:500 }}>{t.ticket_number}</td>
                <td style={{ padding:"11px 12px", fontSize:12, color:COLORS.text2, direction:"ltr" }}>{formatDisplayPhone(getLivePhoneFromTicket(t))}</td>
                <td style={{ padding:"11px 12px", fontSize:12 }}>{t.type}</td>
                <td style={{ padding:"11px 12px" }}>
                  <span style={{ fontSize:11, padding:"3px 9px", borderRadius:10, background:COLORS.greenLight, color:COLORS.green, border:`1px solid ${COLORS.borderHard}` }}>{t.status}</span>
                </td>
                <td style={{ padding:"11px 12px", fontSize:12, color:COLORS.text2 }}>{new Date(t.created_at).toLocaleDateString("ar-SA")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// EMPLOYEE PERFORMANCE — تقرير أداء الموظفين
// ══════════════════════════════════════════════════════════════
function EmployeePerformancePage({ currentUser }) {
  const [data, setData] = useState(null);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [selectedUser, setSelectedUser] = useState(null);
  const [actLoading, setActLoading] = useState(false);

  const loadPerformance = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/employee-performance?days=${days}`, { headers: authHeaders() });
      const d = await res.json();
      setData(d);
    } catch { setData(null); }
    setLoading(false);
  };

  const loadUserActivities = async (userId) => {
    setActLoading(true);
    try {
      const res = await fetch(`${API}/activities?days=${days}&userId=${userId}`, { headers: authHeaders() });
      const d = await res.json();
      setActivities(Array.isArray(d) ? d : []);
    } catch { setActivities([]); }
    setActLoading(false);
  };

  useEffect(() => { loadPerformance(); }, [days]);

  useEffect(() => {
    if (selectedUser) loadUserActivities(selectedUser);
    else setActivities([]);
  }, [selectedUser, days]);

  const employees = data?.employees || [];

  const actionIcon = (action) => {
    if (action.includes("دخول")) return "🔑";
    if (action.includes("رسالة")) return "💬";
    if (action.includes("إغلاق")) return "🔒";
    if (action.includes("تحويل")) return "🤝";
    if (action.includes("استطلاع")) return "📊";
    if (action.includes("تذكرة")) return "🎫";
    return "📋";
  };

  return (
    <div style={S.page}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:4 }}>
        <div style={S.pageTitle}>أداء الموظفين</div>
        <div style={{ display:"flex", gap:8 }}>
          {[{l:"آخر 7 أيام",v:7},{l:"آخر 30 يوم",v:30},{l:"آخر 90 يوم",v:90}].map(p=>(
            <button key={p.v} style={{ ...S.btn(days===p.v?"primary":"ghost"), padding:"5px 12px", fontSize:12 }} onClick={()=>setDays(p.v)}>{p.l}</button>
          ))}
        </div>
      </div>
      <div style={S.pageSub}>تقرير مفصّل لأداء كل موظف — الردود، الإغلاقات، التحويلات وأوقات الدخول</div>

      {loading ? <div style={{ textAlign:"center", padding:40, color:COLORS.text2 }}>جارٍ التحميل...</div> : !data ? <div style={{ textAlign:"center", padding:40, color:COLORS.text3 }}>تعذر تحميل البيانات</div> : (<>
        {/* إحصائيات عامة */}
        <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:14, marginBottom:24 }}>
          {[
            { label:"إجمالي الأنشطة", value:data.totalActivities, color:COLORS.blue, icon:Activity },
            { label:"عدد الموظفين النشطين", value:employees.length, color:COLORS.green, icon:Users },
            { label:"إجمالي الردود", value:employees.reduce((s,e)=>s+e.replies,0), color:COLORS.purple, icon:MessageSquare },
            { label:"إجمالي الإغلاقات", value:employees.reduce((s,e)=>s+e.closures,0), color:COLORS.orange, icon:Lock },
          ].map(s=>(
            <div key={s.label} style={S.card}>
              <div style={{ marginBottom:8 }}><s.icon size={22} color={s.color} /></div>
              <div style={{ fontSize:11, color:COLORS.text2, marginBottom:4 }}>{s.label}</div>
              <div style={{ fontSize:26, fontWeight:600, color:s.color }}>{s.value}</div>
            </div>
          ))}
        </div>

        {/* جدول الموظفين */}
        <div style={{ ...S.card, marginBottom:20 }}>
          <div style={{ fontSize:14, fontWeight:500, marginBottom:14, color:COLORS.text2, display:"flex", alignItems:"center", gap:6 }}>
            <Users size={16} /> أداء كل موظف
          </div>
          <table style={{ width:"100%", borderCollapse:"collapse" }}>
            <thead>
              <tr style={{ borderBottom:`1px solid ${COLORS.border}` }}>
                {["الموظف","الدور","مرات الدخول","الردود","الإغلاقات","التحويلات","التذاكر","آخر دخول","إجمالي",""].map(h=>(
                  <th key={h} style={{ padding:"10px 12px", textAlign:"right", fontSize:12, color:COLORS.text2, fontWeight:500 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {employees.length===0 ? <tr><td colSpan={10} style={{ textAlign:"center", padding:24, color:COLORS.text3 }}>لا توجد أنشطة في هذه الفترة</td></tr>
              : employees.map(emp=>(
                <tr key={emp.userId} style={{ borderBottom:`1px solid ${COLORS.border}`, background:selectedUser===emp.userId?"rgba(37,211,102,0.06)":"transparent", cursor:"pointer" }} onClick={()=>setSelectedUser(selectedUser===emp.userId?null:emp.userId)}>
                  <td style={{ padding:"11px 12px", fontSize:13, fontWeight:500 }}>{emp.userName}</td>
                  <td style={{ padding:"11px 12px" }}>
                    <span style={{ fontSize:11, padding:"2px 8px", borderRadius:10, background:emp.userRole==="admin"?"rgba(188,140,255,0.12)":"rgba(88,166,255,0.1)", color:emp.userRole==="admin"?COLORS.purple:COLORS.blue }}>{emp.userRole==="admin"?"مدير":"موظف"}</span>
                  </td>
                  <td style={{ padding:"11px 12px", fontSize:13, color:COLORS.blue, fontWeight:500 }}>{emp.logins}</td>
                  <td style={{ padding:"11px 12px", fontSize:13, color:COLORS.green, fontWeight:500 }}>{emp.replies}</td>
                  <td style={{ padding:"11px 12px", fontSize:13, color:COLORS.orange, fontWeight:500 }}>{emp.closures}</td>
                  <td style={{ padding:"11px 12px", fontSize:13, color:COLORS.purple, fontWeight:500 }}>{emp.handoffs}</td>
                  <td style={{ padding:"11px 12px", fontSize:13, color:"#e0a030", fontWeight:500 }}>{emp.ticketUpdates||0}</td>
                  <td style={{ padding:"11px 12px", fontSize:12, color:COLORS.text2 }}>{emp.lastLogin ? new Date(emp.lastLogin).toLocaleString("ar-SA",{year:"numeric",month:"2-digit",day:"2-digit",hour:"2-digit",minute:"2-digit"}) : "—"}</td>
                  <td style={{ padding:"11px 12px", fontSize:13, fontWeight:600, color:COLORS.text }}>{emp.totalActions}</td>
                  <td style={{ padding:"11px 12px", fontSize:11, color:COLORS.green }}>{selectedUser===emp.userId?"▲ إخفاء":"▼ التفاصيل"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* تفاصيل أنشطة الموظف المحدد */}
        {selectedUser && (
          <div style={{ ...S.card, marginBottom:20 }}>
            <div style={{ fontSize:14, fontWeight:500, marginBottom:14, color:COLORS.text2, display:"flex", alignItems:"center", gap:6 }}>
              <ClipboardList size={16} /> سجل أنشطة: {employees.find(e=>e.userId===selectedUser)?.userName || "—"}
            </div>
            {actLoading ? <div style={{ textAlign:"center", padding:20, color:COLORS.text2 }}>جارٍ التحميل...</div>
            : activities.length===0 ? <div style={{ textAlign:"center", padding:20, color:COLORS.text3 }}>لا توجد أنشطة</div>
            : (
              <div style={{ maxHeight:400, overflowY:"auto" }}>
                <table style={{ width:"100%", borderCollapse:"collapse" }}>
                  <thead>
                    <tr style={{ borderBottom:`1px solid ${COLORS.border}`, position:"sticky", top:0, background:COLORS.surface }}>
                      {["","النشاط","التفاصيل","التاريخ والوقت"].map(h=>(
                        <th key={h} style={{ padding:"10px 12px", textAlign:"right", fontSize:12, color:COLORS.text2, fontWeight:500 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {activities.map((a,i)=>(
                      <tr key={a.id||i} style={{ borderBottom:`1px solid ${COLORS.border}` }}>
                        <td style={{ padding:"10px 12px", fontSize:16, textAlign:"center", width:36 }}>{actionIcon(a.action)}</td>
                        <td style={{ padding:"10px 12px", fontSize:13, fontWeight:500, color:COLORS.text }}>{a.action}</td>
                        <td style={{ padding:"10px 12px", fontSize:12, color:COLORS.text2, maxWidth:400, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{a.details || "—"}</td>
                        <td style={{ padding:"10px 12px", fontSize:12, color:COLORS.text2 }}>{new Date(a.created_at).toLocaleString("ar-SA",{year:"numeric",month:"2-digit",day:"2-digit",hour:"2-digit",minute:"2-digit",second:"2-digit"})}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </>)}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// TICKETS PAGE — قسم التذاكر المستقل
// ══════════════════════════════════════════════════════════════
function TicketsPage({ currentUser }) {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("الكل");
  const [search, setSearch] = useState("");
  const [typeView, setTypeView] = useState("الكل");
  const [selectedTicketId, setSelectedTicketId] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  useEffect(() => {
    setLoading(true);
    fetch(`${API}/tickets`)
      .then(r => r.json())
      .then(d => {
        const list = Array.isArray(d) ? d : [];
        setTickets(list);
        setSelectedTicketId(list[0]?.id || null);
      })
      .catch(() => {
        setTickets([]);
        setSelectedTicketId(null);
      })
      .finally(() => setLoading(false));
  }, []);

  const filtered = tickets.filter(t => {
    const d = new Date(t.created_at);
    const now = new Date();
    const periodMatch = period === "الكل"
      ? true
      : period === "اليوم"
        ? d.toDateString() === now.toDateString()
        : period === "الأسبوع"
          ? (now - d) < 7 * 24 * 60 * 60 * 1000
          : d.getMonth() === now.getMonth();

    const q = search.trim().toLowerCase();
    const searchMatch = !q || [
      t.ticket_number,
      t.customer_phone,
      getLivePhoneFromTicket(t),
      t.type,
      t.status,
      t.description,
    ].some(v => String(v || "").toLowerCase().includes(q));

    return periodMatch && searchMatch;
  });

  const statusSummary = TICKET_TYPE_ORDER.map((type) => {
    const rows = filtered.filter((t) => normalizeTicketType(t.type) === type);
    return {
      type,
      rows,
      total: rows.length,
      newCount: rows.filter((t) => t.status === "جديد").length,
      inProgressCount: rows.filter((t) => t.status === "قيد المعالجة").length,
      doneCount: rows.filter((t) => t.status === "تمت المعالجة" || t.status === "مغلق").length,
    };
  });

  const visibleSections = typeView === "الكل"
    ? statusSummary.filter((item) => item.total > 0)
    : statusSummary.filter((item) => item.type === typeView);

  const visibleTickets = visibleSections.flatMap((item) => item.rows);
  const selected = visibleTickets.find(t => t.id === selectedTicketId) || visibleTickets[0] || null;

  const updateTicketStatus = async (ticketId, status) => {
    if (!ticketId || !status) return;
    setUpdatingStatus(true);
    try {
      const res = await fetch(`${API}/tickets/${ticketId}`, {
        method: "PATCH",
        headers: authHeaders(),
        body: JSON.stringify({ status }),
      });
      const updated = await res.json();
      if (!res.ok) throw new Error(updated?.error || "status_update_failed");
      setTickets(prev => prev.map(t => (t.id === ticketId ? { ...t, ...updated } : t)));
      if (currentUser) logActivity(currentUser, "تحديث تذكرة", `تذكرة ${updated?.ticket_number || ticketId} → ${status}`);
    } catch {
      alert("❌ تعذر تحديث حالة التذكرة");
    }
    setUpdatingStatus(false);
  };

  const parseSalesTicketDetails = (description = "") => {
    const txt = String(description || "");
    const name = (txt.match(/الاسم:\s*(.+)/) || [])[1] || "-";
    const phone = (txt.match(/رقم الجوال:\s*(.+)/) || [])[1] || "-";
    const request = (txt.match(/المطلوب:\s*(.+)/) || [])[1] || "-";
    const quantity = (txt.match(/الكمية:\s*(.+)/) || [])[1] || "-";
    return { name: name.trim(), phone: phone.trim(), request: request.trim(), quantity: quantity.trim() };
  };

  const sales = parseSalesTicketDetails(selected?.description || "");

  return (
    <div style={S.page}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:4 }}>
        <div style={S.pageTitle}>التذاكر</div>
        <div style={{ display:"flex", gap:8 }}>
          {["اليوم","الأسبوع","الشهر","الكل"].map(p=>(
            <button key={p} style={{ ...S.btn(period===p?"primary":"ghost"), padding:"5px 12px", fontSize:12 }} onClick={()=>setPeriod(p)}>{p}</button>
          ))}
        </div>
      </div>
      <div style={{ ...S.pageSub }}>قسم مستقل لعرض تفاصيل التذاكر بدون التحويل إلى المحادثات</div>

      <div style={{ ...S.card, marginBottom:12, padding:"10px 12px" }}>
        <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
          <button
            style={{ ...S.btn(typeView==="الكل"?"primary":"ghost"), padding:"4px 10px", fontSize:12 }}
            onClick={() => setTypeView("الكل")}
          >
            الكل ({filtered.length})
          </button>
          {statusSummary.map((item) => (
            <button
              key={item.type}
              style={{ ...S.btn(typeView===item.type?"primary":"ghost"), padding:"4px 10px", fontSize:12 }}
              onClick={() => setTypeView(item.type)}
            >
              {ticketTypeLabel(item.type)} ({item.total})
            </button>
          ))}
        </div>
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"repeat(5,minmax(0,1fr))", gap:10, marginBottom:12 }}>
        {statusSummary.map((item) => (
          <div key={item.type} style={{ ...S.card, padding:"10px 12px" }}>
            <div style={{ fontSize:13, fontWeight:600, marginBottom:8 }}>{ticketTypeLabel(item.type)}</div>
            <div style={{ fontSize:11, color:COLORS.text2, display:"flex", justifyContent:"space-between" }}>
              <span>الإجمالي</span>
              <b>{item.total}</b>
            </div>
            <div style={{ fontSize:11, color:COLORS.text2, display:"flex", justifyContent:"space-between" }}>
              <span>جديد</span>
              <b>{item.newCount}</b>
            </div>
            <div style={{ fontSize:11, color:COLORS.text2, display:"flex", justifyContent:"space-between" }}>
              <span>قيد المعالجة</span>
              <b>{item.inProgressCount}</b>
            </div>
            <div style={{ fontSize:11, color:COLORS.text2, display:"flex", justifyContent:"space-between" }}>
              <span>تمت المعالجة</span>
              <b>{item.doneCount}</b>
            </div>
          </div>
        ))}
      </div>

      <div style={{ ...S.card, marginBottom:12, padding:"10px 12px" }}>
        <input
          style={S.input}
          placeholder="ابحث برقم التذكرة أو العميل أو النوع أو الحالة"
          value={search}
          onChange={e=>setSearch(e.target.value)}
        />
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"1.2fr 1fr", gap:14 }}>
        <div style={S.card}>
          <div style={{ fontSize:14, fontWeight:500, marginBottom:12, color:COLORS.text2 }}>قائمة التذاكر ({visibleTickets.length})</div>
          {loading ? <div style={{ color:COLORS.text3, textAlign:"center", padding:20 }}>جارٍ التحميل...</div>
          : visibleTickets.length===0 ? <div style={{ color:COLORS.text3, textAlign:"center", padding:20 }}>لا توجد تذاكر</div>
          : (
            <div style={{ display:"flex", flexDirection:"column", gap:8, maxHeight:"calc(100vh - 320px)", overflowY:"auto" }}>
              {visibleSections.map((section) => (
                <div key={section.type} style={{ border:`1px solid ${COLORS.border}`, borderRadius:10, padding:"8px 10px", background:COLORS.surface2 }}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
                    <div style={{ fontSize:12, fontWeight:700, color:COLORS.text }}>{ticketTypeLabel(section.type)}</div>
                    <div style={{ display:"flex", gap:6, fontSize:10 }}>
                      <span style={{ padding:"2px 6px", borderRadius:10, background:COLORS.surface, border:`1px solid ${COLORS.border}` }}>جديد: {section.newCount}</span>
                      <span style={{ padding:"2px 6px", borderRadius:10, background:COLORS.surface, border:`1px solid ${COLORS.border}` }}>قيد المعالجة: {section.inProgressCount}</span>
                      <span style={{ padding:"2px 6px", borderRadius:10, background:COLORS.surface, border:`1px solid ${COLORS.border}` }}>تمت المعالجة: {section.doneCount}</span>
                    </div>
                  </div>
                  <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                    {section.rows.map((t) => (
                      <div
                        key={t.id}
                        onClick={() => setSelectedTicketId(t.id)}
                        style={{
                          border:`1px solid ${selected?.id===t.id ? COLORS.borderHard : COLORS.border}`,
                          borderRadius:10,
                          padding:"10px 12px",
                          background:selected?.id===t.id ? COLORS.greenLight : COLORS.surface,
                          cursor:"pointer",
                        }}
                      >
                        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", gap:8 }}>
                          <div style={{ fontSize:13, fontWeight:600, color:COLORS.green }}>{t.ticket_number}</div>
                          <span style={{ fontSize:10, padding:"2px 8px", borderRadius:10, background:COLORS.surface2, border:`1px solid ${COLORS.border}`, color:COLORS.text2 }}>{t.status}</span>
                        </div>
                        <div style={{ fontSize:12, marginTop:3 }}>{ticketTypeLabel(normalizeTicketType(t.type))}</div>
                        <div style={{ fontSize:11, color:COLORS.text2, marginTop:3, direction:"ltr" }}>{formatDisplayPhone(getLivePhoneFromTicket(t))}</div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={S.card}>
          <div style={{ fontSize:14, fontWeight:500, marginBottom:12, color:COLORS.text2 }}>تفاصيل التذكرة</div>
          {!selected ? <div style={{ color:COLORS.text3, textAlign:"center", padding:20 }}>اختر تذكرة من القائمة</div>
          : (
            <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
              <div><b>رقم التذكرة:</b> {selected.ticket_number || "-"}</div>
              <div><b>النوع:</b> {selected.type || "-"}</div>
              <div>
                <b>الحالة:</b>
                <select
                  style={{ ...S.input, width:180, display:"inline-block", marginRight:8, padding:"6px 10px", height:34 }}
                  value={selected.status || "جديد"}
                  onChange={(e)=>updateTicketStatus(selected.id, e.target.value)}
                  disabled={updatingStatus}
                >
                  <option value="جديد">جديد</option>
                  <option value="قيد المعالجة">قيد المعالجة</option>
                  <option value="تمت المعالجة">تمت المعالجة</option>
                </select>
              </div>
              <div><b>العميل:</b> <span style={{ direction:"ltr", display:"inline-block" }}>{formatDisplayPhone(getLivePhoneFromTicket(selected))}</span></div>
              <div><b>التاريخ:</b> {selected.created_at ? new Date(selected.created_at).toLocaleString("ar-SA") : "-"}</div>

              {["عروض_الاسعار", "مبيعات_عرض_سعر"].includes(selected.type) && (
                <div style={{ marginTop:8, padding:"10px 12px", border:`1px solid ${COLORS.border}`, borderRadius:8, background:COLORS.surface2 }}>
                  <div style={{ fontSize:12, color:COLORS.text2, marginBottom:5 }}>تفاصيل عرض السعر</div>
                  <div style={{ fontSize:12 }}><b>الاسم:</b> {sales.name}</div>
                  <div style={{ fontSize:12 }}><b>الجوال:</b> <span style={{ direction:"ltr", display:"inline-block" }}>{formatDisplayPhone(sales.phone)}</span></div>
                  <div style={{ fontSize:12 }}><b>المطلوب:</b> {sales.request}</div>
                  <div style={{ fontSize:12 }}><b>الكمية:</b> {sales.quantity}</div>
                </div>
              )}

              <div style={{ marginTop:8 }}>
                <div style={{ fontSize:12, color:COLORS.text2, marginBottom:5 }}>الوصف الكامل</div>
                <div style={{ whiteSpace:"pre-wrap", fontSize:12, lineHeight:1.6, background:COLORS.surface2, border:`1px solid ${COLORS.border}`, borderRadius:8, padding:"10px 12px" }}>
                  {selected.description || "لا يوجد وصف"}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// USERS MANAGEMENT — إدارة المستخدمين عبر الخادم
// ══════════════════════════════════════════════════════════════
function UsersPage({ currentUser }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name:"", email:"", role:"agent" });
  const [msg, setMsg] = useState("");

  const showMsg = (m) => { setMsg(m); setTimeout(()=>setMsg(""), 3000); };

  const loadUsers = async () => {
    try {
      const res = await fetch(`${API}/users`, { headers: authHeaders() });
      if (res.ok) { const d = await res.json(); setUsers(Array.isArray(d) ? d : []); }
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { loadUsers(); }, []);

  const addUser = async () => {
    if (!form.name || !form.email) { showMsg("❌ الاسم والبريد مطلوبان"); return; }
    try {
      const res = await fetch(`${API}/users`, { method:"POST", headers: authHeaders(), body: JSON.stringify(form) });
      const data = await res.json();
      if (!res.ok) { showMsg(`❌ ${data.error || "فشل الإضافة"}`); return; }
      logActivity(currentUser, "إضافة مستخدم", form.name);
      const emailStatus = data.emailDelivery?.sent ? "✅ تم إرسال بيانات الدخول على البريد" : "⚠️ تم الإضافة لكن لم يتم إرسال البريد";
      setForm({ name:"", email:"", role:"agent" });
      setShowAdd(false);
      showMsg(emailStatus);
      loadUsers();
    } catch { showMsg("❌ خطأ في الاتصال بالخادم"); }
  };

  const toggleActive = async (id) => {
    const u = users.find(x=>x.id===id);
    try {
      await fetch(`${API}/users/${id}`, { method:"PATCH", headers: authHeaders(), body: JSON.stringify({ active: !u.active }) });
      logActivity(currentUser, u.active?"إيقاف مستخدم":"تفعيل مستخدم", u.name);
      loadUsers();
    } catch { showMsg("❌ خطأ في التحديث"); }
  };

  const deleteUser = async (id) => {
    const u = users.find(x=>x.id===id);
    if (!window.confirm(`هل تريد حذف ${u?.name}؟`)) return;
    try {
      const res = await fetch(`${API}/users/${id}`, { method:"DELETE", headers: authHeaders() });
      const data = await res.json();
      if (!res.ok) { showMsg(`❌ ${data.error || "فشل الحذف"}`); return; }
      logActivity(currentUser, "حذف مستخدم", u.name);
      showMsg("✅ تم حذف المستخدم");
      loadUsers();
    } catch { showMsg("❌ خطأ في الاتصال"); }
  };

  const resetPassword = async (userId) => {
    const u = users.find(x=>x.id===userId);
    if (!window.confirm(`هل تريد إعادة تعيين كلمة المرور لـ ${u?.name}؟\nسيتم إنشاء كلمة مرور جديدة وإرسالها على البريد.`)) return;
    try {
      const res = await fetch(`${API}/users/${userId}/password`, { method:"PATCH", headers: authHeaders(), body: JSON.stringify({}) });
      const data = await res.json();
      if (!res.ok) { showMsg("❌ فشل إعادة تعيين كلمة المرور"); return; }
      logActivity(currentUser, "إعادة تعيين كلمة مرور", u?.name);
      const emailStatus = data.emailDelivery?.sent ? "✅ تم إعادة تعيين كلمة المرور وإرسالها على البريد" : "⚠️ تم التعيين لكن لم يتم إرسال البريد";
      showMsg(emailStatus);
    } catch { showMsg("❌ خطأ في الاتصال"); }
  };

  return (
    <div style={S.page}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:4 }}>
        <div style={S.pageTitle}>إدارة المستخدمين</div>
        <button style={S.btn("primary")} onClick={()=>setShowAdd(!showAdd)}>+ مستخدم جديد</button>
      </div>
      <div style={{ ...S.pageSub }}>{loading ? "جارٍ التحميل..." : `${users.length} مستخدم مسجّل`}</div>

      {msg && <div style={{ padding:"10px 14px", borderRadius:8, background:msg.includes("✅")?"rgba(37,211,102,0.1)":"rgba(248,81,73,0.1)", border:`1px solid ${msg.includes("✅")?COLORS.borderHard:"rgba(248,81,73,0.25)"}`, fontSize:13, color:msg.includes("✅")?COLORS.green:COLORS.red, marginBottom:16 }}>{msg}</div>}

      {showAdd && (
        <div style={{ ...S.card, marginBottom:16 }}>
          <div style={{ fontSize:14, fontWeight:500, marginBottom:14, color:COLORS.text2 }}>إضافة مستخدم جديد</div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:12 }}>
            <div><div style={{ fontSize:12, color:COLORS.text2, marginBottom:5 }}>الاسم</div><input style={S.input} placeholder="محمد العمري" value={form.name} onChange={e=>setForm({...form,name:e.target.value})} /></div>
            <div><div style={{ fontSize:12, color:COLORS.text2, marginBottom:5 }}>البريد</div><input style={S.input} type="email" placeholder="user@ostar.sa" value={form.email} onChange={e=>setForm({...form,email:e.target.value})} /></div>
            <div><div style={{ fontSize:12, color:COLORS.text2, marginBottom:5 }}>الدور</div>
              <select style={{ ...S.input }} value={form.role} onChange={e=>setForm({...form,role:e.target.value})}>
                <option value="agent">موظف</option><option value="admin">مدير</option>
              </select>
            </div>
          </div>
          <div style={{ fontSize:11, color:COLORS.text3, marginBottom:10 }}>📩 كلمة المرور ستُنشأ تلقائياً (6 أرقام) وتُرسل على بريد الموظف</div>
          <div style={{ display:"flex", gap:8 }}><button style={S.btn("primary")} onClick={addUser}>حفظ</button><button style={S.btn("ghost")} onClick={()=>setShowAdd(false)}>إلغاء</button></div>
        </div>
      )}

      <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
        {users.map(u=>(
          <div key={u.id} style={{ ...S.card, display:"flex", alignItems:"center", gap:14, padding:"14px 18px" }}>
            <div style={{ width:40, height:40, borderRadius:"50%", background:COLORS.greenLight, border:`1px solid ${COLORS.borderHard}`, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, fontSize:16 }}>
              {u.role==="admin"?<Shield size={16} color={COLORS.blue} />:<User size={16} color={COLORS.green} />}
            </div>
            <div style={{ flex:1 }}>
              <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:3 }}>
                <div style={{ fontSize:14, fontWeight:500 }}>{u.name}</div>
                <span style={{ fontSize:10, padding:"2px 8px", borderRadius:10, background:u.role==="admin"?"rgba(88,166,255,0.12)":COLORS.greenLight, color:u.role==="admin"?COLORS.blue:COLORS.green, border:`1px solid ${u.role==="admin"?"rgba(88,166,255,0.3)":COLORS.borderHard}` }}>{u.role==="admin"?"مدير":"موظف"}</span>
                {u.id===currentUser.id && <span style={{ fontSize:10, color:COLORS.yellow }}>أنت</span>}
              </div>
              <div style={{ fontSize:12, color:COLORS.text2 }}>📧 {u.email}</div>
            </div>
            <div style={{ display:"flex", alignItems:"center", gap:6 }}>
              <div style={{ width:7, height:7, borderRadius:"50%", background:u.active?COLORS.green:COLORS.text3 }} />
              <span style={{ fontSize:12, color:u.active?COLORS.green:COLORS.text3 }}>{u.active?"نشط":"موقوف"}</span>
            </div>
            <div style={{ display:"flex", gap:6 }}>
              <button style={{ ...S.btn("ghost"), padding:"5px 10px", display:"inline-flex", alignItems:"center", justifyContent:"center" }} onClick={()=>resetPassword(u.id)} title="إعادة تعيين كلمة المرور"><Shield size={14} /></button>
              {u.id!==currentUser.id && <>
                <button style={{ ...S.btn(u.active?"danger":"primary"), padding:"5px 10px", fontSize:12 }} onClick={()=>toggleActive(u.id)}>{u.active?"إيقاف":"تفعيل"}</button>
                <button style={{ ...S.btn("danger"), padding:"5px 10px", display:"inline-flex", alignItems:"center", justifyContent:"center" }} onClick={()=>deleteUser(u.id)}><Trash2 size={14} /></button>
              </>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// ACTIVITY LOG
// ══════════════════════════════════════════════════════════════
function ActivityPage({ currentUser }) {
  const [logs, setLogs] = useState([]);
  const [search, setSearch] = useState("");

  useEffect(() => { setLogs(JSON.parse(localStorage.getItem("activityLogs")||"[]")); }, []);

  const filtered = logs.filter(l => {
    const matchRole = currentUser.role==="admin" || l.userId===currentUser.id;
    const matchSearch = !search || l.userName.includes(search) || l.action.includes(search) || l.details.includes(search);
    return matchRole && matchSearch;
  });

  return (
    <div style={S.page}>
      <div style={S.pageTitle}>سجل النشاط</div>
      <div style={{ ...S.pageSub }}>{currentUser.role==="admin"?"جميع أنشطة الفريق":"نشاطاتك الخاصة"} — {filtered.length} سجل</div>
      <div style={{ marginBottom:16 }}>
        <input style={{ ...S.input, maxWidth:320 }} placeholder="بحث في السجلات..." value={search} onChange={e=>setSearch(e.target.value)} />
      </div>
      <div style={S.card}>
        {filtered.length===0 ? <div style={{ color:COLORS.text3, textAlign:"center", padding:24 }}>لا توجد سجلات</div>
        : filtered.map((log,i)=>(
          <div key={log.id} style={{ display:"flex", alignItems:"center", gap:12, padding:"12px 0", borderBottom:i<filtered.length-1?`1px solid ${COLORS.border}`:"none" }}>
            <div style={{ width:34, height:34, borderRadius:"50%", background:COLORS.greenLight, border:`1px solid ${COLORS.borderHard}`, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, fontSize:14 }}>
              {log.action.includes("دخول")?<Shield size={14} color={COLORS.green} />:log.action.includes("إغلاق")?<Lock size={14} color={COLORS.green} />:log.action.includes("تحويل")?<UserCog size={14} color={COLORS.green} />:<ClipboardList size={14} color={COLORS.green} />}
            </div>
            <div style={{ flex:1 }}>
              <div style={{ fontSize:13, color:COLORS.text }}><b>{log.userName}</b> — {log.action}</div>
              {log.details && <div style={{ fontSize:12, color:COLORS.text2 }}>{log.details}</div>}
            </div>
            <div style={{ fontSize:11, color:COLORS.text3, textAlign:"left", direction:"ltr", flexShrink:0 }}>
              {new Date(log.time).toLocaleString("ar-SA")}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// AI DEMO
// ══════════════════════════════════════════════════════════════
function AIDemo() {
  const [messages, setMessages] = useState([{ role:"assistant", content:"أهلاً بك في نجوم العمران 🌟 كيف أقدر أساعدك اليوم؟" }]);
  const [input, setInput] = useState(""); const [loading, setLoading] = useState(false);
  const endRef = useRef(null);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior:"smooth" }); }, [messages]);
  const send = async () => {
    if (!input.trim()||loading) return;
    const txt=input.trim(); setInput("");
    const nm=[...messages,{role:"user",content:txt}]; setMessages(nm); setLoading(true);
    try {
      const r=await fetch(`${API}/ai/chat`,{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({messages:nm})});
      const d=await r.json(); setMessages(p=>[...p,{role:"assistant",content:d.reply||"عذراً حدث خطأ"}]);
    } catch { setMessages(p=>[...p,{role:"assistant",content:"عذراً، خطأ في الاتصال."}]); }
    setLoading(false);
  };
  return (
    <div style={S.page}>
      <div style={S.pageTitle}>تجربة الذكاء</div>
      <div style={{ ...S.pageSub }}>اختبر ردود المساعد الذكي</div>
      <div style={{ ...S.card, display:"flex", flexDirection:"column", height:"calc(100vh - 185px)" }}>
        <div style={{ padding:"12px 16px", borderBottom:`1px solid ${COLORS.border}`, background:COLORS.surface2, display:"flex", alignItems:"center", gap:10, borderRadius:"12px 12px 0 0" }}>
          <div style={{ width:36, height:36, borderRadius:"50%", background:COLORS.greenLight, border:`1px solid ${COLORS.borderHard}`, display:"flex", alignItems:"center", justifyContent:"center" }}><Bot size={18} color={COLORS.green} /></div>
          <div><div style={{ fontSize:14, fontWeight:500 }}>مساعد نجوم العمران</div><div style={{ fontSize:12, color:loading?COLORS.yellow:COLORS.green }}>{loading?"يكتب...":"متصل"}</div></div>
        </div>
        <div style={{ flex:1, overflowY:"auto", padding:"16px 20px", display:"flex", flexDirection:"column", gap:10, background:COLORS.surface2, minHeight:0 }}>
          {messages.map((m,i)=>(
            <div key={i} style={{ display:"flex", justifyContent:m.role==="user"?"flex-start":"flex-end" }}>
              <div style={{ maxWidth:"72%", padding:"10px 14px", borderRadius:14, fontSize:13, lineHeight:1.6, background:m.role==="user"?COLORS.surface2:COLORS.greenLight, border:m.role==="assistant"?`1px solid ${COLORS.borderHard}`:"none", color:COLORS.text }}>{m.content}</div>
            </div>
          ))}
          {loading && <div style={{ display:"flex", justifyContent:"flex-end" }}><div style={{ padding:"10px 14px", borderRadius:14, background:COLORS.greenLight, border:`1px solid ${COLORS.borderHard}`, color:COLORS.text2 }}>يكتب...</div></div>}
          <div ref={endRef} />
        </div>
        <div style={{ padding:"12px 16px", borderTop:`1px solid ${COLORS.border}`, display:"flex", gap:8 }}>
          <input style={{ ...S.input, flex:1 }} placeholder="اكتب رسالة كعميل..." value={input} onChange={e=>setInput(e.target.value)} onKeyDown={e=>e.key==="Enter"&&send()} />
          <button style={{ background:COLORS.green, color:"#000", border:"none", borderRadius:8, width:42, height:42, cursor:"pointer", display:"inline-flex", alignItems:"center", justifyContent:"center", flexShrink:0 }} onClick={send} disabled={loading}><Send size={16} /></button>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// PROFILE
// ══════════════════════════════════════════════════════════════
function ProfilePage({ currentUser, onUpdate }) {
  const [form, setForm] = useState({ name:currentUser.name, email:currentUser.email });
  const [pwd, setPwd] = useState({ newPwd:"", confirm:"" });
  const [msg, setMsg] = useState("");
  const showMsg = (m) => { setMsg(m); setTimeout(()=>setMsg(""),3000); };

  const saveProfile = async () => {
    try {
      const res = await fetch(`${API}/users/${currentUser.id}`, {
        method:"PATCH", headers: authHeaders(), body: JSON.stringify({ name: form.name })
      });
      if (!res.ok) { showMsg("❌ فشل حفظ التغييرات"); return; }
      onUpdate({...currentUser,...form});
      logActivity(currentUser,"تعديل الملف الشخصي","");
      showMsg("✅ تم الحفظ");
    } catch { showMsg("❌ خطأ في الاتصال"); }
  };

  const changePassword = async () => {
    if (pwd.newPwd !== pwd.confirm) { showMsg("❌ كلمتا المرور غير متطابقتين"); return; }
    if (pwd.newPwd.length < 6) { showMsg("❌ كلمة المرور قصيرة جداً"); return; }
    try {
      const res = await fetch(`${API}/users/${currentUser.id}/password`, {
        method:"PATCH", headers: authHeaders(), body: JSON.stringify({ password: pwd.newPwd })
      });
      if (!res.ok) { showMsg("❌ فشل تغيير كلمة المرور"); return; }
      logActivity(currentUser,"تغيير كلمة المرور","");
      setPwd({newPwd:"",confirm:""});
      showMsg("✅ تم التغيير");
    } catch { showMsg("❌ خطأ في الاتصال"); }
  };

  return (
    <div style={{ ...S.page, maxWidth:580 }}>
      <div style={S.pageTitle}>الملف الشخصي</div>
      <div style={{ ...S.pageSub }}>تعديل بياناتك الشخصية</div>
      {msg && <div style={{ padding:"9px 14px", borderRadius:8, background:msg.includes("✅")?"rgba(37,211,102,0.1)":"rgba(248,81,73,0.1)", border:`1px solid ${msg.includes("✅")?COLORS.borderHard:"rgba(248,81,73,0.25)"}`, fontSize:13, color:msg.includes("✅")?COLORS.green:COLORS.red, marginBottom:16 }}>{msg}</div>}
      <div style={{ ...S.card, marginBottom:16 }}>
        <div style={{ fontSize:14, fontWeight:500, marginBottom:14, color:COLORS.text2 }}>المعلومات الشخصية</div>
        <div style={{ marginBottom:12 }}><div style={{ fontSize:12, color:COLORS.text2, marginBottom:5 }}>الاسم</div><input style={S.input} value={form.name} onChange={e=>setForm({...form,name:e.target.value})} /></div>
        <div style={{ marginBottom:14 }}><div style={{ fontSize:12, color:COLORS.text2, marginBottom:5 }}>البريد</div><input style={{ ...S.input, opacity:0.6 }} type="email" value={form.email} disabled /></div>
        <button style={S.btn("primary")} onClick={saveProfile}>حفظ التغييرات</button>
      </div>
      <div style={S.card}>
        <div style={{ fontSize:14, fontWeight:500, marginBottom:14, color:COLORS.text2 }}>تغيير كلمة المرور</div>
        {[["الجديدة","newPwd"],["تأكيد الجديدة","confirm"]].map(([label,key])=>(
          <div key={key} style={{ marginBottom:12 }}><div style={{ fontSize:12, color:COLORS.text2, marginBottom:5 }}>{label}</div><input style={S.input} type="password" value={pwd[key]} onChange={e=>setPwd({...pwd,[key]:e.target.value})} /></div>
        ))}
        <button style={{ ...S.btn("ghost"), border:`1px solid ${COLORS.borderHard}`, color:COLORS.green }} onClick={changePassword}>تغيير كلمة المرور</button>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// WHATSAPP QR PAGE — نسخة محسنة مع زر إعادة الربط
// ══════════════════════════════════════════════════════════════
function WhatsAppPage({ activeWhatsAppNumberId, onSelectWhatsAppNumber }) {
  const [status, setStatus] = useState(null);
  const [qr, setQr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resetLoading, setResetLoading] = useState(false);
  const [waNumbers, setWaNumbers] = useState([]);
  const [numbersLoading, setNumbersLoading] = useState(false);
  const [savingNumber, setSavingNumber] = useState(false);
  const [numberForm, setNumberForm] = useState({ name:"", phone_number:"" });
  const [qrModal, setQrModal] = useState({ open:false, numberId:null, title:"", qr:null, status:"idle", phone:null, ownerName:null, error:null, errorAt:null });
  const intervalRef = useRef(null);
  const qrPollRef = useRef(null);
  const numbersIntervalRef = useRef(null);
  const qrAutoGenerateRef = useRef(new Set());

  const closeQrModal = () => {
    if (qrPollRef.current) clearTimeout(qrPollRef.current);
    if (qrModal.numberId) qrAutoGenerateRef.current.delete(qrModal.numberId);
    setQrModal({ open:false, numberId:null, title:"", qr:null, status:"idle", phone:null, ownerName:null, error:null, errorAt:null });
  };

  const numberStatusLabel = (s, isActive) => {
    if (isActive === false) return { text:"مفصول", color:COLORS.red, bg:"rgba(248,81,73,0.12)", border:"rgba(248,81,73,0.25)" };
    const map = {
      ready: { text:"متصل", color:COLORS.green, bg:"rgba(37,211,102,0.12)", border:"rgba(37,211,102,0.25)" },
      waiting_qr: { text:"ينتظر المسح", color:COLORS.yellow, bg:"rgba(210,153,34,0.12)", border:"rgba(210,153,34,0.25)" },
      initializing: { text:"جارٍ التهيئة", color:COLORS.blue, bg:"rgba(88,166,255,0.12)", border:"rgba(88,166,255,0.25)" },
      disconnected: { text:"منقطع", color:COLORS.red, bg:"rgba(248,81,73,0.12)", border:"rgba(248,81,73,0.25)" },
      auth_failure: { text:"فشل الربط", color:COLORS.red, bg:"rgba(248,81,73,0.12)", border:"rgba(248,81,73,0.25)" },
      error: { text:"خطأ", color:COLORS.red, bg:"rgba(248,81,73,0.12)", border:"rgba(248,81,73,0.25)" },
    };
    return map[s] || { text:"غير مفعّل", color:COLORS.text3, bg:COLORS.surface2, border:COLORS.border };
  };

  const looksLikeRealPhone = (value) => {
    const normalized = String(value || "").replace(/\D/g, "");
    return normalized.length >= 9 && normalized.length <= 15 && /^(966|05|5|2|1|7|8|9)/.test(normalized);
  };

  const normalizeSaudiWhatsappNumber = (value) => {
    const digits = String(value || "").replace(/\D/g, "");
    if (!digits) return "";
    if (digits.startsWith("966")) return digits;
    if (digits.startsWith("05")) return `966${digits.slice(1)}`;
    if (digits.startsWith("5") && digits.length === 9) return `966${digits}`;
    return digits;
  };

  const isValidSaudiWhatsappNumber = (value) => /^9665\d{8}$/.test(String(value || ""));
  const isValidPhoneNumberId = (value) => /^\d{8,20}$/.test(String(value || ""));

  const getVisibleNumber = (numberItem) => {
    if (numberItem?.session_phone) return numberItem.session_phone;
    if (looksLikeRealPhone(numberItem?.phone_number)) {
      return numberItem.phone_number;
    }
    if (looksLikeRealPhone(numberItem?.phone_number_id)) return numberItem.phone_number_id;
    return numberItem?.phone_number || numberItem?.phone_number_id || null;
  };

  const fetchStatus = async () => {
    try {
      const res = await fetch(`${API}/whatsapp/status`);
      const data = await res.json();
      setStatus(data);
      if (data.hasQr) {
        const qrRes = await fetch(`${API}/whatsapp/qr`);
        const qrData = await qrRes.json();
        setQr(qrData.qr);
      } else {
        setQr(null);
      }
    } catch(e) {
      console.error("خطأ في جلب الحالة:", e);
    }
    setLoading(false);
  };

  const loadWaNumbers = async (silent = false) => {
    if (!silent) setNumbersLoading(true);
    try {
      const res = await fetch(`${API}/whatsapp-numbers`);
      const data = await res.json();
      setWaNumbers(Array.isArray(data) ? data : []);
    } catch {
      setWaNumbers([]);
    }
    if (!silent) setNumbersLoading(false);
  };

  const addWaNumber = async () => {
    const normalizedPhone = normalizeSaudiWhatsappNumber(numberForm.phone_number);
    const name = numberForm.name.trim();

    if (!name || !normalizedPhone) {
      alert("اكتب الاسم ورقم الواتساب");
      return;
    }

    if (!isValidSaudiWhatsappNumber(normalizedPhone)) {
      alert("صيغة رقم الواتساب يجب أن تكون: 9665XXXXXXXX أو 05XXXXXXXX");
      return;
    }

    // phone_number_id يُولَّد تلقائياً من رقم الهاتف (لا داعي للمستخدم يدخله)
    const payload = {
      name,
      phone_number: normalizedPhone,
      phone_number_id: normalizedPhone, // نستخدم رقم الهاتف كـ ID فريد
    };

    setSavingNumber(true);
    try {
      const res = await fetch(`${API}/whatsapp-numbers`, {
        method:"POST",
        headers:{"Content-Type":"application/json"},
        body:JSON.stringify(payload)
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body.error || "save_failed");
      setNumberForm({ name:"", phone_number:"" });
      await loadWaNumbers();
      onSelectWhatsAppNumber(body.phone_number_id || body.phone_number, body.name || payload.name);
    } catch (e) {
      alert(`❌ تعذر حفظ الرقم\n${e?.message || ""}`);
    }
    setSavingNumber(false);
  };

  const disconnectWaNumber = async (n) => {
    if (!window.confirm(`هل تريد فصل "${n.name}" وإنهاء جلسته؟`)) return;
    try {
      await fetch(`${API}/whatsapp-numbers/${n.id}/disconnect`, { method:"POST" });
      await loadWaNumbers();
      if (activeWhatsAppNumberId !== "all" && n.phone_number_id === activeWhatsAppNumberId) {
        onSelectWhatsAppNumber("all", "كل الأرقام");
      }
    } catch {
      alert("❌ تعذر فصل الرقم");
    }
  };

  const fetchNumberQrState = async (numberId, title) => {
    try {
      const res = await fetch(`${API}/whatsapp-numbers/${numberId}/qr`);
      const data = await res.json();
      setQrModal(prev => ({
        ...prev,
        open:true,
        numberId,
        title,
        qr:data.qr || null,
        status:data.status || "idle",
        phone:data.phone || null,
        ownerName:data.ownerName || null,
        error:data.error || null,
        errorAt:data.errorAt || null,
      }));

      if (["waiting_qr", "initializing"].includes(data.status) && !data.qr) {
        if (qrPollRef.current) clearTimeout(qrPollRef.current);
        qrPollRef.current = setTimeout(() => fetchNumberQrState(numberId, title), 2500);
        return;
      }

      // إذا الحالة idle بدون QR، جرّب التوليد تلقائياً مرة واحدة.
      if ((data.status === "idle" || !data.status) && !data.qr && !qrAutoGenerateRef.current.has(numberId)) {
        qrAutoGenerateRef.current.add(numberId);
        await generateQrForNumber({ id: numberId, name: title }, { auto: true });
      }
    } catch {}
  };

  const generateQrForNumber = async (n, options = {}) => {
    const isAuto = options.auto === true;
    try {
      setQrModal({ open:true, numberId:n.id, title:n.name, qr:null, status:"initializing", phone:null, ownerName:null, error:null, errorAt:null });
      const res = await fetch(`${API}/whatsapp-numbers/${n.id}/generate-qr`, { method:"POST" });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(body.error || "qr_generation_failed");
      }

      setQrModal(prev => ({
        ...prev,
        open:true,
        numberId:n.id,
        title:n.name,
        qr:body.qr || null,
        status:body.status || "initializing",
        phone:body.phone || null,
        ownerName:body.ownerName || null,
        error:body.error || null,
        errorAt:body.errorAt || null,
      }));

      await loadWaNumbers();
      fetchNumberQrState(n.id, n.name);
    } catch (e) {
      if (!isAuto) {
        alert(`❌ تعذر توليد الباركود لهذا الرقم\n${e.message || ""}`);
      }
    }
  };

  const disconnectNumberSession = async (n) => {
    await disconnectWaNumber(n);
  };

  const deleteWaNumber = async (n) => {
    if (!window.confirm(`هل تريد حذف "${n.name}" نهائياً؟ لا يمكن التراجع.`)) return;
    try {
      const res = await fetch(`${API}/whatsapp-numbers/${n.id}`, { method: "DELETE", headers: authOnlyHeaders() });
      if (!res.ok) { const b = await res.json().catch(()=>({})); alert(`❌ ${b.error || "فشل الحذف"}`); return; }
      await loadWaNumbers();
      if (activeWhatsAppNumberId !== "all" && n.phone_number_id === activeWhatsAppNumberId) {
        onSelectWhatsAppNumber("all", "كل الأرقام");
      }
    } catch {
      alert("❌ تعذر حذف الرقم");
    }
  };

  // وظيفة إعادة ضبط الاتصال (تسجيل الخروج من الواتساب)
  const resetConnection = async () => {
    if (!window.confirm("هل أنت متأكد من إعادة ربط واتساب؟ سيتم قطع الاتصال الحالي وتوليد QR جديد.")) return;
    setResetLoading(true);
    try {
      const res = await fetch(`${API}/whatsapp/reset`, { method: "POST" });
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        throw new Error(errBody.error || "reset_failed");
      }
      alert("✅ تم قطع الاتصال. جاري توليد QR جديد...");
      // انتظر قليلاً ثم حدث الحالة
      setTimeout(() => {
        fetchStatus();
        setResetLoading(false);
      }, 3000);
    } catch (e) {
      alert("❌ فشل إعادة الربط. تأكد من أن الخادم يعمل ولا توجد نسخة أخرى من البوت.");
      setResetLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
    loadWaNumbers();
    intervalRef.current = setInterval(fetchStatus, 5000);
    numbersIntervalRef.current = setInterval(() => loadWaNumbers(true), 2000);
    return () => {
      clearInterval(intervalRef.current);
      clearInterval(numbersIntervalRef.current);
      if (qrPollRef.current) clearTimeout(qrPollRef.current);
    };
  }, []);

  const statusLabel = (s) => ({
    "ready": { text: "متصل", color: COLORS.green },
    "waiting_qr": { text: "في انتظار المسح", color: COLORS.yellow },
    "initializing": { text: "جارٍ التهيئة...", color: COLORS.blue },
    "disconnected": { text: "منقطع", color: COLORS.red },
  }[s] || { text: s || "غير معروف", color: COLORS.text2 });

  const st = statusLabel(status?.status);

  return (
    <div style={S.page}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
        <div style={S.pageTitle}>ربط واتساب</div>
        {/* زر إعادة الربط (يظهر دائماً) */}
        <button 
          style={{ ...S.btn("danger"), padding: "6px 14px", fontSize: 12 }} 
          onClick={resetConnection}
          disabled={resetLoading}
        >
          <RefreshCw size={14} /> {resetLoading ? "جاري..." : "إعادة ربط واتساب"}
        </button>
      </div>
      <div style={S.pageSub}>اربط رقم الشركة عبر مسح QR Code</div>

      {/* حالة الاتصال */}
      <div style={{ ...S.card, marginBottom: 20, display: "flex", alignItems: "center", gap: 14 }}>
        <div style={{ width: 14, height: 14, borderRadius: "50%", background: st.color, boxShadow: `0 0 8px ${st.color}`, flexShrink: 0 }} />
        <div>
          <div style={{ fontSize: 15, fontWeight: 500, color: COLORS.text }}>حالة الاتصال</div>
          <div style={{ fontSize: 13, color: st.color, marginTop: 2 }}>{st.text}</div>
        </div>
        <button style={{ ...S.btn("ghost"), marginRight: "auto", padding: "6px 14px", fontSize: 12 }} onClick={fetchStatus}>
          <RefreshCw size={14} /> تحديث
        </button>
      </div>

      {/* عرض المحتوى حسب الحالة */}
      {loading ? (
        <div style={{ ...S.card, textAlign: "center", padding: "40px 20px" }}>
          <div style={{ fontSize: 13, color: COLORS.text2 }}>جارٍ التحميل...</div>
        </div>
      ) : status?.status === "ready" ? (
        <div style={{ ...S.card, textAlign: "center", padding: "40px 20px" }}>
          <div style={{ marginBottom: 12, display:"flex", justifyContent:"center" }}><CheckCircle2 size={44} color={COLORS.green} /></div>
          <div style={{ fontSize: 18, fontWeight: 500, color: COLORS.green, marginBottom: 6 }}>واتساب متصل بنجاح!</div>
          <div style={{ fontSize: 13, color: COLORS.text2 }}>البوت يعمل ويستقبل الرسائل</div>
          <div style={{ marginTop: 20 }}>
            <button style={S.btn("danger")} onClick={resetConnection} disabled={resetLoading}>
              {resetLoading ? "⏳ جاري..." : "قطع الاتصال وربط رقم آخر"}
            </button>
          </div>
        </div>
      ) : status?.hasQr && qr ? (
        <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: 24, alignItems: "start" }}>
          {/* QR Code */}
          <div style={{ ...S.card, textAlign: "center", padding: 20 }}>
            <div style={{ fontSize: 12, color: COLORS.text2, marginBottom: 12 }}>امسح هذا الكود</div>
            <img src={qr} alt="QR Code" style={{ width: 220, height: 220, borderRadius: 8, border: `1px solid ${COLORS.border}` }} />
            <div style={{ fontSize: 11, color: COLORS.text3, marginTop: 10 }}>يتجدد كل 20 ثانية</div>
          </div>

          {/* خطوات المسح */}
          <div style={S.card}>
            <div style={{ fontSize: 14, fontWeight: 500, color: COLORS.text, marginBottom: 16 }}>كيفية المسح</div>
            {[
              { step: "١", text: 'افتح واتساب على جوالك' },
              { step: "٢", text: 'اضغط النقاط ⋮ (أندرويد) أو الإعدادات ⚙️ (iPhone)' },
              { step: "٣", text: 'اختر "الأجهزة المرتبطة"' },
              { step: "٤", text: 'اضغط "ربط جهاز"' },
              { step: "٥", text: 'امسح QR Code الموجود على اليسار' },
            ].map(s => (
              <div key={s.step} style={{ display: "flex", gap: 12, alignItems: "flex-start", marginBottom: 14 }}>
                <div style={{ width: 28, height: 28, borderRadius: "50%", background: COLORS.greenLight, border: `1px solid ${COLORS.borderHard}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 600, color: COLORS.green, flexShrink: 0 }}>
                  {s.step}
                </div>
                <div style={{ fontSize: 13, color: COLORS.text2, paddingTop: 4 }}>{s.text}</div>
              </div>
            ))}
            <div style={{ marginTop: 16, padding: "10px 14px", background: "rgba(210,153,34,0.1)", border: "1px solid rgba(210,153,34,0.25)", borderRadius: 8, fontSize: 12, color: COLORS.yellow }}>
              ⚠️ QR يتغير كل 20 ثانية — امسح بسرعة
            </div>
          </div>
        </div>
      ) : (
        <div style={{ ...S.card, textAlign: "center", padding: "40px 20px" }}>
          <div style={{ fontSize: 13, color: COLORS.text2 }}>
            {status?.status === "initializing" ? "جارٍ تهيئة WhatsApp... انتظر لحظة" : "لا يوجد QR Code حالياً. اضغط 'إعادة ربط واتساب' أعلاه."}
          </div>
        </div>
      )}

      <div style={{ ...S.card, marginTop:20 }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14 }}>
          <div style={{ fontSize:15, fontWeight:500 }}>الأرقام المربوطة</div>
          <button style={{ ...S.btn("ghost"), padding:"6px 12px", fontSize:12 }} onClick={loadWaNumbers}>
            <RefreshCw size={14} /> تحديث القائمة
          </button>
        </div>

        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr auto", gap:8, marginBottom:14 }}>
          <input style={S.input} placeholder="اسم الرقم (مثال: خالد)" value={numberForm.name} onChange={e=>setNumberForm({ ...numberForm, name:e.target.value })} />
          <input
            style={S.input}
            placeholder="رقم الواتساب (05XXXXXXXX أو 9665XXXXXXXX)"
            value={numberForm.phone_number}
            onChange={e=>setNumberForm({ ...numberForm, phone_number:e.target.value.replace(/[^0-9+]/g, "") })}
          />
          <button style={{ ...S.btn("primary"), justifyContent:"center" }} onClick={addWaNumber} disabled={savingNumber}>{savingNumber ? "جاري..." : "إضافة"}</button>
        </div>
        <div style={{ fontSize:11, color:COLORS.text3, marginBottom:10 }}>
          أدخل الاسم والرقم ثم اضغط إضافة — سيظهر باركود لمسحه بالواتساب مباشرةً.
        </div>

        {numbersLoading ? (
          <div style={{ fontSize:12, color:COLORS.text2 }}>جارٍ تحميل الأرقام...</div>
        ) : waNumbers.length === 0 ? (
          <div style={{ fontSize:12, color:COLORS.text3 }}>لا توجد أرقام محفوظة حتى الآن.</div>
        ) : (
          <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
            <button
              style={{ ...S.btn(activeWhatsAppNumberId === "all" ? "blue" : "ghost"), justifyContent:"space-between" }}
              onClick={() => onSelectWhatsAppNumber("all", "كل الأرقام")}
            >
              <span>كل الأرقام</span>
              <span style={{ fontSize:11, color:COLORS.text3 }}>عرض المحادثات بدون تصفية</span>
            </button>
            {waNumbers.map((n) => {
              const sl = numberStatusLabel(n.qr_status, n.is_active);
              const visibleNum = getVisibleNumber(n);
              const isSelected = activeWhatsAppNumberId === n.phone_number_id;
              return (
                <div key={n.id} style={{
                  border: `1px solid ${isSelected ? COLORS.green : COLORS.border}`,
                  borderRadius: 12,
                  background: isSelected ? "rgba(31,157,116,0.05)" : COLORS.surface,
                  overflow: "hidden",
                }}>
                  {/* رأس البطاقة */}
                  <div style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 14px", borderBottom:`1px solid ${COLORS.border}` }}>
                    <span style={{ width:9, height:9, borderRadius:"50%", background:sl.color, flexShrink:0, boxShadow:`0 0 6px ${sl.color}` }} />
                    <span style={{ fontWeight:600, fontSize:14, color:COLORS.text, flex:1 }}>{n.name}</span>
                    <span style={{ fontSize:11, borderRadius:20, padding:"2px 10px", color:sl.color, background:sl.bg, border:`1px solid ${sl.border}`, fontWeight:500 }}>
                      {sl.text}
                    </span>
                    {isSelected && <span style={{ fontSize:11, color:COLORS.green, background:"rgba(31,157,116,0.1)", borderRadius:20, padding:"2px 10px", border:`1px solid rgba(31,157,116,0.2)` }}>✓ مختار</span>}
                  </div>

                  {/* معلومات الرقم */}
                  <div style={{ padding:"8px 14px", display:"flex", gap:20, flexWrap:"wrap" }}>
                    <div style={{ fontSize:12, color:COLORS.text2 }}>
                      <span style={{ color:COLORS.text3 }}>الرقم: </span>
                      <span style={{ direction:"ltr", display:"inline-block" }}>{visibleNum || <span style={{ color:COLORS.text3, fontStyle:"italic" }}>سيظهر بعد الربط</span>}</span>
                    </div>
                    {n.session_owner_name && (
                      <div style={{ fontSize:12, color:COLORS.text2 }}>
                        <span style={{ color:COLORS.text3 }}>الحساب: </span>
                        <span>{n.session_owner_name}</span>
                      </div>
                    )}
                    {n.has_qr && <span style={{ fontSize:11, color:COLORS.yellow }}>● باركود جاهز للمسح</span>}
                  </div>

                  {/* أزرار الإجراءات */}
                  <div style={{ display:"flex", gap:8, padding:"8px 14px 10px", flexWrap:"wrap" }}>
                    <button
                      style={{ ...S.btn(isSelected ? "blue" : "ghost"), padding:"5px 14px", fontSize:12 }}
                      onClick={() => onSelectWhatsAppNumber(n.phone_number_id, n.name)}
                      disabled={!n.is_active}
                    >
                      {isSelected ? "✓ مختار" : "اختيار"}
                    </button>

                    <button
                      style={{ ...S.btn("yellow"), padding:"5px 14px", fontSize:12 }}
                      onClick={() => {
                        qrAutoGenerateRef.current.delete(n.id);
                        generateQrForNumber(n);
                      }}
                    >
                      <RefreshCw size={13} /> توليد QR
                    </button>

                    <button
                      style={{ ...S.btn("ghost"), padding:"5px 14px", fontSize:12 }}
                      onClick={() => fetchNumberQrState(n.id, n.name)}
                    >
                      <Smartphone size={13} /> عرض QR
                    </button>

                    <button
                      style={{ ...S.btn("danger"), padding:"5px 14px", fontSize:12, marginRight:"auto" }}
                      onClick={() => disconnectWaNumber(n)}
                    >
                      <LogOut size={13} /> فصل
                    </button>

                    <button
                      style={{ ...S.btn("danger"), padding:"5px 10px", fontSize:12, background:"rgba(180,0,0,0.15)", border:"1px solid rgba(220,50,50,0.35)" }}
                      onClick={() => deleteWaNumber(n)}
                      title="حذف الرقم نهائياً"
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {qrModal.open && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.55)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:1000 }} onClick={closeQrModal}>
          <div style={{ ...S.card, width:360, maxWidth:"92vw", textAlign:"center" }} onClick={(e)=>e.stopPropagation()}>
            <div style={{ fontSize:15, fontWeight:600, marginBottom:8 }}>باركود الرقم: {qrModal.title}</div>
            <div style={{ fontSize:12, color:COLORS.text2, marginBottom:12 }}>الحالة: {qrModal.status}</div>
            {qrModal.ownerName && <div style={{ fontSize:12, color:COLORS.blue, marginBottom:8 }}>صاحب الحساب: {qrModal.ownerName}</div>}
            {qrModal.phone && <div style={{ fontSize:12, color:COLORS.green, marginBottom:12, direction:"ltr" }}>المربوط: {qrModal.phone}</div>}
            {qrModal.qr ? (
              <img src={qrModal.qr} alt="QR Code" style={{ width:240, height:240, borderRadius:8, border:`1px solid ${COLORS.border}` }} />
            ) : (
              <div style={{ fontSize:12, color:COLORS.text3, padding:"14px 0" }}>
                {qrModal.status === "initializing" || qrModal.status === "waiting_qr"
                  ? "جاري تجهيز الباركود... انتظر ثواني ثم سيتم تحديثه تلقائيًا"
                  : qrModal.status === "ready"
                    ? "الرقم مربوط بالفعل، لذلك لا يظهر باركود جديد إلا بعد فصل الجلسة أو إعادة الربط."
                    : qrModal.status === "error"
                      ? `فشل إنشاء الجلسة: ${qrModal.error || "سبب غير معروف"}`
                      : "لا يوجد باركود حاليًا، اضغط توليد باركود."}
              </div>
            )}
            {qrModal.status === "error" && qrModal.errorAt && (
              <div style={{ marginTop:8, fontSize:11, color:COLORS.red }}>
                وقت الخطأ: {new Date(qrModal.errorAt).toLocaleString("ar-SA")}
              </div>
            )}
            <div style={{ display:"flex", gap:8, justifyContent:"center", marginTop:14 }}>
              <button
                style={{ ...S.btn("yellow"), fontSize:12 }}
                onClick={() => {
                  if (qrModal.numberId) {
                    qrAutoGenerateRef.current.delete(qrModal.numberId);
                    generateQrForNumber({ id: qrModal.numberId, name: qrModal.title });
                  }
                }}
              >
                <RefreshCw size={14} /> توليد QR
              </button>
              <button style={{ ...S.btn("ghost"), fontSize:12 }} onClick={() => fetchNumberQrState(qrModal.numberId, qrModal.title)}>
                <RefreshCw size={14} /> تحديث
              </button>
              <button style={{ ...S.btn("danger"), fontSize:12 }} onClick={closeQrModal}>
                إغلاق
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// 🎁 صفحة العروض
// ══════════════════════════════════════════════════════════════
function OffersPage() {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editOffer, setEditOffer] = useState(null);
  const [form, setForm] = useState({ title: "", description: "", discount_text: "", valid_until: "" });
  const [error, setError] = useState("");

  const loadOffers = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/offers`, { headers: authHeaders() });
      const data = await res.json();
      setOffers(Array.isArray(data) ? data : []);
    } catch { setOffers([]); }
    setLoading(false);
  };

  useEffect(() => { loadOffers(); }, []);

  const openAdd = () => {
    setEditOffer(null);
    setForm({ title: "", description: "", discount_text: "", valid_until: "" });
    setError("");
    setShowForm(true);
  };

  const openEdit = (o) => {
    setEditOffer(o);
    setForm({ title: o.title || "", description: o.description || "", discount_text: o.discount_text || "", valid_until: o.valid_until || "" });
    setError("");
    setShowForm(true);
  };

  const closeForm = () => { setShowForm(false); setEditOffer(null); setError(""); };

  const handleSave = async () => {
    if (!form.title.trim()) { setError("العنوان مطلوب"); return; }
    setSaving(true);
    setError("");
    try {
      const url = editOffer ? `${API}/offers/${editOffer.id}` : `${API}/offers`;
      const method = editOffer ? "PUT" : "POST";
      const res = await fetch(url, { method, headers: { ...authHeaders(), "Content-Type": "application/json" }, body: JSON.stringify(form) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "خطأ في الحفظ");
      await loadOffers();
      closeForm();
    } catch (e) { setError(e.message); }
    setSaving(false);
  };

  const handleToggle = async (o) => {
    try {
      await fetch(`${API}/offers/${o.id}`, { method: "PUT", headers: { ...authHeaders(), "Content-Type": "application/json" }, body: JSON.stringify({ is_active: !o.is_active }) });
      await loadOffers();
    } catch {}
  };

  const handleDelete = async (o) => {
    if (!window.confirm(`حذف العرض "${o.title}"؟`)) return;
    try {
      await fetch(`${API}/offers/${o.id}`, { method: "DELETE", headers: authHeaders() });
      await loadOffers();
    } catch {}
  };

  const activeCount = offers.filter(o => o.is_active).length;

  return (
    <div style={{ padding: 24, maxWidth: 800 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 700 }}>العروض والتخفيضات 🎁</div>
          <div style={{ fontSize: 12, color: COLORS.text2, marginTop: 2 }}>
            {activeCount} عرض نشط من أصل {offers.length} — يردّ عليها البوت تلقائياً
          </div>
        </div>
        <button style={S.btn("primary")} onClick={openAdd}>
          <Gift size={15} /> إضافة عرض جديد
        </button>
      </div>

      {loading ? (
        <div style={{ color: COLORS.text2, textAlign: "center", padding: 40 }}>جاري التحميل...</div>
      ) : offers.length === 0 ? (
        <div style={{ textAlign: "center", padding: 60, color: COLORS.text2, border: `1px dashed ${COLORS.border}`, borderRadius: 12 }}>
          <Gift size={40} style={{ marginBottom: 12, opacity: 0.3 }} />
          <div>ما في عروض مضافة بعد</div>
          <div style={{ fontSize: 12, marginTop: 4 }}>أضف أول عرض وسيظهر للعملاء عبر البوت</div>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {offers.map(o => (
            <div key={o.id} style={{ border: `1px solid ${o.is_active ? COLORS.green : COLORS.border}`, borderRadius: 10, padding: "14px 16px", background: COLORS.surface, opacity: o.is_active ? 1 : 0.6 }}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                    <span style={{ fontWeight: 600, fontSize: 14 }}>{o.title}</span>
                    <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 10, background: o.is_active ? COLORS.greenLight : COLORS.surface2, color: o.is_active ? COLORS.green : COLORS.text2, border: `1px solid ${o.is_active ? COLORS.green : COLORS.border}` }}>
                      {o.is_active ? "نشط" : "متوقف"}
                    </span>
                  </div>
                  {o.discount_text && <div style={{ fontSize: 13, color: COLORS.green, fontWeight: 500 }}>💰 {o.discount_text}</div>}
                  {o.description && <div style={{ fontSize: 12, color: COLORS.text2, marginTop: 4 }}>{o.description}</div>}
                  {o.valid_until && <div style={{ fontSize: 11, color: COLORS.text2, marginTop: 6 }}>📅 ينتهي: {new Date(o.valid_until).toLocaleDateString("ar-SA", { day: "numeric", month: "long", year: "numeric" })}</div>}
                </div>
                <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  <button style={{ ...S.btn("ghost"), padding: "5px 10px", fontSize: 12 }} onClick={() => handleToggle(o)}>
                    {o.is_active ? "إيقاف" : "تفعيل"}
                  </button>
                  <button style={{ ...S.btn("ghost"), padding: "5px 8px" }} onClick={() => openEdit(o)} title="تعديل">
                    <Pencil size={13} />
                  </button>
                  <button style={{ ...S.btn("danger"), padding: "5px 8px" }} onClick={() => handleDelete(o)} title="حذف">
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
          <div style={{ background: COLORS.surface, borderRadius: 14, padding: 28, width: 460, boxShadow: "0 8px 32px rgba(0,0,0,0.15)" }}>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 20 }}>
              {editOffer ? "تعديل العرض" : "إضافة عرض جديد"} 🎁
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div>
                <label style={{ fontSize: 12, color: COLORS.text2, display: "block", marginBottom: 4 }}>عنوان العرض *</label>
                <input style={{ ...S.input, width: "100%", boxSizing: "border-box" }} placeholder="مثال: عرض مكيفات AUX" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />
              </div>
              <div>
                <label style={{ fontSize: 12, color: COLORS.text2, display: "block", marginBottom: 4 }}>نص الخصم</label>
                <input style={{ ...S.input, width: "100%", boxSizing: "border-box" }} placeholder="مثال: خصم 20% على جميع المكيفات" value={form.discount_text} onChange={e => setForm(f => ({ ...f, discount_text: e.target.value }))} />
              </div>
              <div>
                <label style={{ fontSize: 12, color: COLORS.text2, display: "block", marginBottom: 4 }}>تفاصيل إضافية</label>
                <textarea style={{ ...S.input, width: "100%", boxSizing: "border-box", resize: "vertical", minHeight: 70 }} placeholder="تفاصيل العرض اللي تريد البوت يذكرها للعميل..." value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
              </div>
              <div>
                <label style={{ fontSize: 12, color: COLORS.text2, display: "block", marginBottom: 4 }}>تاريخ انتهاء العرض (اختياري)</label>
                <input type="date" style={{ ...S.input, width: "100%", boxSizing: "border-box" }} value={form.valid_until} onChange={e => setForm(f => ({ ...f, valid_until: e.target.value }))} />
              </div>
              {error && <div style={{ color: COLORS.red, fontSize: 12, padding: "6px 10px", background: "#fff0f0", borderRadius: 6 }}>{error}</div>}
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 20 }}>
              <button style={S.btn("ghost")} onClick={closeForm}>إلغاء</button>
              <button style={S.btn("primary")} onClick={handleSave} disabled={saving}>
                {saving ? "جاري الحفظ..." : editOffer ? "حفظ التعديلات" : "إضافة العرض"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PriceSheetsPage({ currentUser }) {
  const [files, setFiles] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [uniqueProducts, setUniqueProducts] = useState(0);
  const [changedToday, setChangedToday] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [replaceMode, setReplaceMode] = useState(true);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [changes, setChanges] = useState([]);
  const [changesLoading, setChangesLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [rowHeaders, setRowHeaders] = useState([]);
  const [rowsLoading, setRowsLoading] = useState(false);
  const [rowsPage, setRowsPage] = useState(1);
  const [rowsTotalPages, setRowsTotalPages] = useState(1);
  const [rowsTotal, setRowsTotal] = useState(0);
  const [rowsLatestOnly, setRowsLatestOnly] = useState(false);
  const [rowsQuery, setRowsQuery] = useState("");
  const [savingPriceColumnId, setSavingPriceColumnId] = useState("");
  const [showAllColumns, setShowAllColumns] = useState(false);

  const preferredColumnKeywords = [
    "اسم", "name", "model", "barcode", "brand", "productcode", "sku",
    "سعر", "price", "cost", "tabby", "tamara", "discount",
  ];
  const keyHeaders = rowHeaders.filter((h) => {
    const n = String(h || "").toLowerCase();
    return preferredColumnKeywords.some((k) => n.includes(k));
  });
  const displayedHeaders = showAllColumns
    ? rowHeaders
    : (keyHeaders.length ? keyHeaders : rowHeaders.slice(0, 6));

  const fileReplyColumnsMap = new Map((files || []).map((f) => [String(f.id), Array.isArray(f.approvedReplyColumns) ? f.approvedReplyColumns : []]));

  const buildReplyText = (row) => {
    if (!row || typeof row !== "object") return "";
    const cols = row.columns && typeof row.columns === "object" ? row.columns : {};
    const fileSelected = fileReplyColumnsMap.get(String(row.fileId || "")) || [];
    const selected = fileSelected.length ? fileSelected : ["اسم الصنف", "Name", "model", "Brand", "Barcode", "سعر البيع", "السعر قبل الخصم", "نسبة الخصم"];
    const lines = [];

    for (const c of selected) {
      const v = cols[c];
      const text = String(v ?? "").trim();
      if (!text) continue;
      lines.push(`${c}: ${text}`);
    }

    if (!lines.length) {
      if (row.product) lines.push(`المنتج: ${row.product}`);
      if (row.price !== null && row.price !== undefined && row.price !== "") lines.push(`السعر: ${row.price} ريال`);
    }

    return lines.join("\n");
  };

  const copyReplyText = async (row) => {
    const text = buildReplyText(row);
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      alert("✅ تم نسخ الرد");
    } catch {
      alert("❌ تعذر النسخ");
    }
  };

  const loadSheets = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/price-sheets`, { headers: authHeaders() });
      const data = await res.json();
      setFiles(Array.isArray(data.files) ? data.files : []);
      setTotalRows(Number(data.totalRows || 0));
      setUniqueProducts(Number(data.uniqueProducts || 0));
      setChangedToday(Number(data.changedToday || 0));
    } catch {
      setFiles([]);
      setTotalRows(0);
      setUniqueProducts(0);
      setChangedToday(0);
    }
    setLoading(false);
  };

  const loadDailyChanges = async () => {
    setChangesLoading(true);
    try {
      const res = await fetch(`${API}/price-sheets/changes?days=1&limit=20`, { headers: authHeaders() });
      const data = await res.json().catch(() => ({}));
      setChanges(Array.isArray(data.changes) ? data.changes : []);
    } catch {
      setChanges([]);
    }
    setChangesLoading(false);
  };

  const loadRows = async ({ page = rowsPage, latestOnly = rowsLatestOnly, q = rowsQuery } = {}) => {
    setRowsLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page),
        limit: "30",
        latestOnly: latestOnly ? "true" : "false",
      });
      if (q.trim()) params.set("q", q.trim());
      const res = await fetch(`${API}/price-sheets/rows?${params.toString()}`, { headers: authHeaders() });
      const data = await res.json().catch(() => ({}));
      setRows(Array.isArray(data.rows) ? data.rows : []);
      setRowHeaders(Array.isArray(data.headers) ? data.headers : []);
      setRowsPage(Number(data.page || page || 1));
      setRowsTotalPages(Number(data.totalPages || 1));
      setRowsTotal(Number(data.total || 0));
    } catch {
      setRows([]);
      setRowHeaders([]);
      setRowsTotal(0);
      setRowsTotalPages(1);
    }
    setRowsLoading(false);
  };

  useEffect(() => {
    if (currentUser?.role !== "admin") return;
    loadSheets();
    loadDailyChanges();
    loadRows({ page: 1, latestOnly: rowsLatestOnly, q: rowsQuery });
  }, [currentUser?.role]);

  const uploadSheets = async () => {
    if (!selectedFiles.length) return;
    setUploading(true);
    try {
      const form = new FormData();
      selectedFiles.forEach((f) => form.append("files", f));
      const mode = replaceMode ? "replace" : "append";
      const res = await fetch(`${API}/price-sheets/upload?mode=${mode}`, {
        method: "POST",
        headers: authOnlyHeaders(),
        body: form,
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || "upload_failed");
      setSelectedFiles([]);
      await loadSheets();
      await loadDailyChanges();
      await loadRows({ page: 1, latestOnly: rowsLatestOnly, q: rowsQuery });
      if (Number(body.changedToday || 0) > 0) {
        alert(`✅ تم تحديث الأسعار. تغيّر اليوم: ${body.changedToday} منتج`);
      }
    } catch (e) {
      alert(`❌ فشل رفع الشيتات: ${e.message || "unknown_error"}`);
    }
    setUploading(false);
  };

  const removeSheet = async (id) => {
    if (!window.confirm("تأكيد حذف ملف الشيت؟")) return;
    try {
      const res = await fetch(`${API}/price-sheets/${id}`, {
        method: "DELETE",
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error("delete_failed");
      await loadSheets();
      await loadDailyChanges();
      await loadRows({ page: 1, latestOnly: rowsLatestOnly, q: rowsQuery });
    } catch {
      alert("❌ تعذر حذف الملف");
    }
  };

  const savePriceColumn = async (fileId, approvedPriceColumn, approvedReplyColumns = []) => {
    setSavingPriceColumnId(fileId);
    try {
      const res = await fetch(`${API}/price-sheets/${fileId}/price-column`, {
        method: "PATCH",
        headers: authHeaders(),
        body: JSON.stringify({ approvedPriceColumn, approvedReplyColumns, strictPriceOnly: true }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || "save_failed");
      await loadSheets();
      await loadDailyChanges();
      await loadRows({ page: 1, latestOnly: rowsLatestOnly, q: rowsQuery });
    } catch (e) {
      alert(`❌ فشل حفظ عمود السعر: ${e.message || "save_failed"}`);
    }
    setSavingPriceColumnId("");
  };

  const toggleReplyColumnForFile = (fileId, columnName) => {
    setFiles((prev) => prev.map((x) => {
      if (x.id !== fileId) return x;
      const current = Array.isArray(x.approvedReplyColumns) ? x.approvedReplyColumns : [];
      const has = current.includes(columnName);
      const next = has ? current.filter((c) => c !== columnName) : [...current, columnName];
      return { ...x, approvedReplyColumns: next };
    }));
  };

  const searchInSheets = async () => {
    if (!query.trim()) {
      setResults([]);
      return;
    }
    setSearching(true);
    try {
      const res = await fetch(`${API}/price-sheets/search?q=${encodeURIComponent(query)}&limit=50`, {
        headers: authHeaders(),
      });
      const body = await res.json().catch(() => ({}));
      setResults(Array.isArray(body.results) ? body.results : []);
    } catch {
      setResults([]);
    }
    setSearching(false);
  };

  if (currentUser?.role !== "admin") {
    return (
      <div style={S.page}>
        <div style={S.pageTitle}>شيتات الأسعار</div>
        <div style={S.pageSub}>هذه الصفحة متاحة للمدير فقط</div>
      </div>
    );
  }

  return (
    <div style={S.page}>
      <div style={S.pageTitle}>شيتات الأسعار</div>
      <div style={S.pageSub}>رفع عدة ملفات Excel/CSV والبحث داخلها</div>

      <div style={{ ...S.card, marginBottom: 16 }}>
        <div style={{ fontSize: 13, color: COLORS.text2, marginBottom: 10 }}>
          الملفات: {files.length} | الصفوف المفهرسة: {totalRows} | المنتجات الحالية: {uniqueProducts} | تغيرات اليوم: {changedToday}
        </div>
        <input
          type="file"
          multiple
          accept=".xlsx,.xls,.csv,.cvs"
          onChange={(e) => setSelectedFiles(Array.from(e.target.files || []))}
        />
        <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10, fontSize: 12, color: COLORS.text2 }}>
          <input type="checkbox" checked={replaceMode} onChange={(e) => setReplaceMode(e.target.checked)} />
          تحديث يومي: استبدال الأسعار القديمة واعتماد آخر شيت فقط
        </label>
        <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
          <button style={S.btn("primary")} onClick={uploadSheets} disabled={uploading || !selectedFiles.length}>
            <FileText size={14} /> {uploading ? "جارٍ الرفع..." : replaceMode ? "تحديث الأسعار اليومية" : "رفع الشيتات"}
          </button>
          <button style={S.btn("ghost")} onClick={loadSheets}>
            <RefreshCw size={14} /> تحديث
          </button>
          <button style={S.btn("ghost")} onClick={loadDailyChanges}>
            <RefreshCw size={14} /> تغيرات اليوم
          </button>
          <button style={S.btn("ghost")} onClick={() => loadRows({ page: 1, latestOnly: rowsLatestOnly, q: rowsQuery })}>
            <RefreshCw size={14} /> تحديث البيانات المستخرجة
          </button>
        </div>
      </div>

      <div style={{ ...S.card, marginBottom: 16 }}>
        <div style={{ display: "flex", gap: 8 }}>
          <input
            style={S.input}
            placeholder="ابحث عن منتج... مثال: مكيف 18"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && searchInSheets()}
          />
          <button style={S.btn("blue")} onClick={searchInSheets} disabled={searching}>
            {searching ? "بحث..." : "بحث"}
          </button>
        </div>

        {results.length > 0 && (
          <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 8 }}>
            {results.map((r, idx) => (
              <div key={`${r.fileId}-${r.sheetName}-${r.rowNumber}-${idx}`} style={{ padding: "10px 12px", border: `1px solid ${COLORS.border}`, borderRadius: 8, background: COLORS.surface2 }}>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{r.product}</div>
                <div style={{ fontSize: 12, color: COLORS.text2 }}>السعر: {r.price} ريال</div>
                {r.priceChange !== null && r.priceChange !== 0 && (
                  <div style={{ fontSize: 11, color: r.priceChange > 0 ? COLORS.red : COLORS.green }}>
                    {r.priceChange > 0 ? "↑" : "↓"} التغير: {Math.abs(r.priceChange)} ريال (السابق: {r.previousPrice})
                  </div>
                )}
                <div style={{ fontSize: 11, color: COLORS.text3 }}>{r.fileName} | {r.sheetName} | صف {r.rowNumber}</div>
                <div style={{ marginTop: 8 }}>
                  <button style={S.btn("ghost")} onClick={() => copyReplyText(r)}>نسخ رد جاهز</button>
                </div>
              </div>
            ))}
          </div>
        )}
        {query.trim() && !searching && results.length === 0 && (
          <div style={{ marginTop: 10, fontSize: 12, color: COLORS.text3 }}>لا توجد نتائج مطابقة.</div>
        )}
      </div>

      <div style={{ ...S.card, marginBottom: 16 }}>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 10 }}>تغيرات الأسعار اليوم</div>
        {changesLoading ? (
          <div style={{ fontSize: 12, color: COLORS.text2 }}>جارٍ التحميل...</div>
        ) : changes.length === 0 ? (
          <div style={{ fontSize: 12, color: COLORS.text3 }}>لا توجد تغيّرات أسعار خلال آخر 24 ساعة.</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {changes.map((c, idx) => (
              <div key={`${c.productNorm || c.product}-${idx}`} style={{ padding: "10px 12px", border: `1px solid ${COLORS.border}`, borderRadius: 8, background: COLORS.surface2 }}>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{c.product}</div>
                <div style={{ fontSize: 12, color: COLORS.text2 }}>
                  {c.previousPrice} ← {c.price} ريال
                  <span style={{ marginRight: 8, color: c.priceChange > 0 ? COLORS.red : COLORS.green }}>
                    ({c.priceChange > 0 ? "+" : ""}{c.priceChange} ريال)
                  </span>
                </div>
                <div style={{ fontSize: 11, color: COLORS.text3 }}>{c.fileName} | {c.sheetName}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={S.card}>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 10 }}>الملفات المرفوعة</div>
        {loading ? (
          <div style={{ fontSize: 12, color: COLORS.text2 }}>جارٍ التحميل...</div>
        ) : files.length === 0 ? (
          <div style={{ fontSize: 12, color: COLORS.text3 }}>لا يوجد ملفات بعد.</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {files.map((f) => (
              <div key={f.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "9px 10px", border: `1px solid ${COLORS.border}`, borderRadius: 8 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.originalName}</div>
                  <div style={{ fontSize: 11, color: COLORS.text3 }}>صفوف: {f.rowsCount || 0}</div>
                  <div style={{ display: "flex", gap: 6, alignItems: "center", marginTop: 6, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 11, color: COLORS.text2 }}>عمود السعر المعتمد:</span>
                    <select
                      value={f.approvedPriceColumn || ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        setFiles((prev) => prev.map((x) => (x.id === f.id ? { ...x, approvedPriceColumn: value } : x)));
                      }}
                      style={{ ...S.input, width: "auto", minWidth: 220, height: 32, padding: "4px 8px" }}
                    >
                      <option value="">(تلقائي)</option>
                      {(Array.isArray(f.columnOptions) ? f.columnOptions : []).map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                    <button
                      style={S.btn("blue")}
                      disabled={savingPriceColumnId === f.id}
                      onClick={() => savePriceColumn(f.id, f.approvedPriceColumn || "", f.approvedReplyColumns || [])}
                    >
                      {savingPriceColumnId === f.id ? "حفظ..." : "اعتماد"}
                    </button>
                  </div>
                  <div style={{ marginTop: 8 }}>
                    <div style={{ fontSize: 11, color: COLORS.text2, marginBottom: 4 }}>أعمدة الرد لهذا الملف</div>
                    <div style={{ border: `1px solid ${COLORS.border}`, borderRadius: 8, padding: 8, maxHeight: 180, overflowY: "auto", background: COLORS.surface2 }}>
                      {(Array.isArray(f.columnOptions) ? f.columnOptions : []).map((c) => {
                        const checked = Array.isArray(f.approvedReplyColumns) && f.approvedReplyColumns.includes(c);
                        return (
                          <label key={`${f.id}-${c}`} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: COLORS.text2, marginBottom: 6, cursor: "pointer" }}>
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleReplyColumnForFile(f.id, c)}
                            />
                            <span>{c}</span>
                          </label>
                        );
                      })}
                    </div>
                    <div style={{ fontSize: 10, color: COLORS.text3, marginTop: 4 }}>تقدر تختار أكثر من عمود مباشرة بدون Ctrl/Cmd</div>
                  </div>
                </div>
                <button style={S.btn("danger")} onClick={() => removeSheet(f.id)}>
                  <Trash2 size={14} /> حذف
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={S.card}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 10, flexWrap: "wrap" }}>
          <div style={{ fontSize: 14, fontWeight: 600 }}>كل البيانات المستخرجة من الشيتات</div>
          <div style={{ fontSize: 12, color: COLORS.text2 }}>الإجمالي: {rowsTotal} صف</div>
        </div>

        <div style={{ display: "flex", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
          <input
            style={{ ...S.input, flex: 1, minWidth: 220 }}
            placeholder="بحث داخل كل أعمدة الشيت..."
            value={rowsQuery}
            onChange={(e) => setRowsQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && loadRows({ page: 1, latestOnly: rowsLatestOnly, q: rowsQuery })}
          />
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: COLORS.text2, padding: "0 6px" }}>
            <input
              type="checkbox"
              checked={rowsLatestOnly}
              onChange={(e) => {
                const v = e.target.checked;
                setRowsLatestOnly(v);
                loadRows({ page: 1, latestOnly: v, q: rowsQuery });
              }}
            />
            عرض أحدث سعر لكل منتج فقط
          </label>
          <button style={S.btn("blue")} onClick={() => loadRows({ page: 1, latestOnly: rowsLatestOnly, q: rowsQuery })}>
            بحث
          </button>
          <button style={S.btn("ghost")} onClick={() => setShowAllColumns((v) => !v)}>
            {showAllColumns ? `عرض أعمدة مهمة فقط (${displayedHeaders.length})` : `عرض كل الأعمدة (${rowHeaders.length})`}
          </button>
        </div>

        <div style={{ marginBottom: 10, padding: "10px 12px", border: `1px solid ${COLORS.border}`, borderRadius: 8, background: COLORS.surface2, fontSize: 12, color: COLORS.text2 }}>
          اختيار أعمدة الرد أصبح لكل ملف من قسم "الملفات المرفوعة" بالأعلى (لأن كل ملف عنده أعمدة سعر/خصم مختلفة).
        </div>

        <div style={{ overflowX: "auto", border: `1px solid ${COLORS.border}`, borderRadius: 8 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 920 }}>
            <thead>
              <tr style={{ background: COLORS.surface2 }}>
                <th style={{ textAlign: "right", fontSize: 12, padding: "8px 10px", borderBottom: `1px solid ${COLORS.border}` }}>المنتج</th>
                <th style={{ textAlign: "right", fontSize: 12, padding: "8px 10px", borderBottom: `1px solid ${COLORS.border}` }}>السعر</th>
                <th style={{ textAlign: "right", fontSize: 12, padding: "8px 10px", borderBottom: `1px solid ${COLORS.border}` }}>المصدر</th>
                {displayedHeaders.map((h) => (
                  <th key={h} style={{ textAlign: "right", fontSize: 12, padding: "8px 10px", borderBottom: `1px solid ${COLORS.border}` }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rowsLoading ? (
                <tr><td colSpan={displayedHeaders.length + 3} style={{ fontSize: 12, color: COLORS.text2, padding: 12 }}>جارٍ تحميل البيانات...</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={displayedHeaders.length + 3} style={{ fontSize: 12, color: COLORS.text3, padding: 12 }}>لا يوجد صفوف مطابقة.</td></tr>
              ) : rows.map((r, idx) => (
                <tr key={`${r.fileId}-${r.sheetName}-${r.rowNumber}-${idx}`}>
                  <td style={{ fontSize: 12, padding: "8px 10px", borderBottom: `1px solid ${COLORS.border}` }}>{r.product}</td>
                  <td style={{ fontSize: 12, padding: "8px 10px", borderBottom: `1px solid ${COLORS.border}` }}>
                    <div>{r.price ?? "-"}</div>
                    {r.priceColumn && <div style={{ fontSize: 10, color: COLORS.text3 }}>من عمود: {r.priceColumn}</div>}
                  </td>
                  <td style={{ fontSize: 11, color: COLORS.text3, padding: "8px 10px", borderBottom: `1px solid ${COLORS.border}` }}>
                    <div>{r.fileName} | {r.sheetName} | {r.rowNumber}</div>
                    <button style={{ ...S.btn("ghost"), marginTop: 6, padding: "4px 8px", fontSize: 11 }} onClick={() => copyReplyText(r)}>نسخ رد</button>
                  </td>
                  {displayedHeaders.map((h) => (
                    <td key={`${h}-${idx}`} style={{ fontSize: 11, padding: "8px 10px", borderBottom: `1px solid ${COLORS.border}`, maxWidth: 220, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={String((r.columns && r.columns[h]) ?? "")}>
                      {String((r.columns && r.columns[h]) ?? "")}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 10 }}>
          <div style={{ fontSize: 12, color: COLORS.text3 }}>صفحة {rowsPage} من {rowsTotalPages}</div>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              style={S.btn("ghost")}
              disabled={rowsPage <= 1 || rowsLoading}
              onClick={() => loadRows({ page: rowsPage - 1, latestOnly: rowsLatestOnly, q: rowsQuery })}
            >
              السابق
            </button>
            <button
              style={S.btn("ghost")}
              disabled={rowsPage >= rowsTotalPages || rowsLoading}
              onClick={() => loadRows({ page: rowsPage + 1, latestOnly: rowsLatestOnly, q: rowsQuery })}
            >
              التالي
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// MAIN APP
// ══════════════════════════════════════════════════════════════
export default function App() {
  const [currentUser, setCurrentUser] = useState(() => {
    const session = getStoredSession();
    return session?.user || null;
  });
  const [page, setPage] = useState(currentUser?.role === "admin" ? "dashboard" : "convs");
  const [activeWhatsAppNumberId, setActiveWhatsAppNumberId] = useState(() => localStorage.getItem("activeWhatsAppNumberId") || "all");
  const [activeWhatsAppNumberName, setActiveWhatsAppNumberName] = useState(() => localStorage.getItem("activeWhatsAppNumberName") || "كل الأرقام");

  const handleLogin = (user) => { setCurrentUser(user); };
  const handleLogout = () => {
    logActivity(currentUser,"تسجيل خروج","");
    clearStoredSession();
    setCurrentUser(null);
  };
  const handleUpdateUser = (u) => {
    setCurrentUser(u);
    const token = localStorage.getItem("authToken");
    if (token) setStoredSession(token, u);
  };
  const handleSelectWhatsAppNumber = (id, name) => {
    const selectedId = id || "all";
    const selectedName = name || "كل الأرقام";
    setActiveWhatsAppNumberId(selectedId);
    setActiveWhatsAppNumberName(selectedName);
    localStorage.setItem("activeWhatsAppNumberId", selectedId);
    localStorage.setItem("activeWhatsAppNumberName", selectedName);

    fetch(`${API}/whatsapp-numbers/select`, {
      method:"POST",
      headers: authHeaders(),
      body:JSON.stringify({ phoneNumberId: selectedId })
    }).catch(() => {});
  };

  // التحقق من صلاحية التوكن عند فتح الصفحة — لو انتهى يُخرج تلقائياً
  useEffect(() => {
    if (!currentUser) return;
    fetch(`${API}/auth/me`, { headers: authHeaders() })
      .then(res => {
        if (res.status === 401 || res.status === 403) {
          clearStoredSession();
          setCurrentUser(null);
        }
      })
      .catch(() => {}); // خطأ شبكة → خلّي المستخدم داخل
  }, []);

  useEffect(() => {
    if (!currentUser) return;
    fetch(`${API}/whatsapp-numbers/select`, {
      method:"POST",
      headers: authHeaders(),
      body:JSON.stringify({ phoneNumberId: activeWhatsAppNumberId })
    }).catch(() => {});
  }, [currentUser, activeWhatsAppNumberId]);

  if (!currentUser) return <LoginPage onLogin={handleLogin} />;

  const isAdmin = currentUser.role === "admin";

  const navItems = isAdmin ? [
    { id:"dashboard",      icon:LayoutDashboard, label:"لوحة التحكم" },
    { id:"convs",          icon:MessageSquare,   label:"المحادثات" },
    { id:"tickets",        icon:Ticket,          label:"التذاكر" },
    { id:"reports",        icon:BarChart3,        label:"التقارير" },
    { id:"empPerformance", icon:Users,            label:"أداء الموظفين" },
    { id:"demo",           icon:Bot,              label:"تجربة الذكاء" },
    { id:"whatsapp",       icon:Smartphone,       label:"ربط واتساب" },
    { id:"offers",         icon:Gift,             label:"العروض" },
    { id:"priceSheets",    icon:FileText,         label:"شيتات الأسعار" },
    { id:"users",          icon:Users,            label:"المستخدمين", divider:true },
    { id:"activity",       icon:Activity,         label:"سجل النشاط" },
    { id:"profile",        icon:User,             label:"ملفي الشخصي" },
  ] : [
    { id:"convs",    icon:MessageSquare, label:"المحادثات" },
    { id:"tickets",  icon:Ticket,        label:"التذاكر" },
    { id:"profile",  icon:User,          label:"ملفي الشخصي" },
  ];

  return (
    <div style={S.app}>
      <div style={S.sidebar}>
        <div style={{ padding:"0 16px 20px", borderBottom:`1px solid ${COLORS.border}`, marginBottom:12 }}>
          <div style={{ fontSize:16, fontWeight:700 }}>OmranHub</div>
          <div style={{ fontSize:11, color:COLORS.text2 }}>نجوم العمران</div>
        </div>
        <div style={{ flex:1 }}>
          {navItems.map(n=>(
            <div key={n.id}>
              {n.divider && <div style={{ height:1, background:COLORS.border, margin:"6px 10px" }} />}
              <div style={S.navItem(page===n.id)} onClick={()=>setPage(n.id)}>
                <n.icon size={16} />
                <span>{n.label}</span>
              </div>
            </div>
          ))}
        </div>
        {/* معلومات المستخدم */}
        <div style={{ padding:"14px 16px", borderTop:`1px solid ${COLORS.border}` }}>
          <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:10 }}>
            <div style={{ width:32, height:32, borderRadius:"50%", background:COLORS.greenLight, border:`1px solid ${COLORS.borderHard}`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:14 }}>
              {currentUser.role==="admin"?<Shield size={14} color={COLORS.blue} />:<User size={14} color={COLORS.green} />}
            </div>
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ fontSize:12, fontWeight:500, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{currentUser.name}</div>
              <div style={{ fontSize:10, color:COLORS.text2 }}>{currentUser.role==="admin"?"مدير":"موظف"}</div>
            </div>
          </div>
          <button style={{ ...S.btn("danger"), width:"100%", justifyContent:"center", padding:"7px", fontSize:12 }} onClick={handleLogout}>
            <LogOut size={14} /> تسجيل خروج
          </button>
        </div>
      </div>
      <div style={S.main}>
        {page==="dashboard"      && isAdmin && <Dashboard onNav={setPage} />}
        {page==="convs"          && <Conversations currentUser={currentUser} activeWhatsAppNumberId={activeWhatsAppNumberId} activeWhatsAppNumberName={activeWhatsAppNumberName} onSelectWhatsAppNumber={handleSelectWhatsAppNumber} />}
        {page==="tickets"        && <TicketsPage currentUser={currentUser} />}
        {page==="reports"        && isAdmin && <Reports />}
        {page==="empPerformance" && isAdmin && <EmployeePerformancePage currentUser={currentUser} />}
        {page==="demo"           && isAdmin && <AIDemo />}
        {page==="whatsapp"       && isAdmin && <WhatsAppPage activeWhatsAppNumberId={activeWhatsAppNumberId} onSelectWhatsAppNumber={handleSelectWhatsAppNumber} />}
        {page==="offers"         && isAdmin && <OffersPage />}
        {page==="priceSheets"    && isAdmin && <PriceSheetsPage currentUser={currentUser} />}
        {page==="users"          && isAdmin && <UsersPage currentUser={currentUser} />}
        {page==="activity"       && isAdmin && <ActivityPage currentUser={currentUser} />}
        {page==="profile"        && <ProfilePage currentUser={currentUser} onUpdate={handleUpdateUser} />}
      </div>
    </div>
  );
}